import ManagementCompanyPicker, { canonicalCompany, CompanyBrand } from './ManagementCompany';
import { BuildingProfileEditor, ProfileUpdated } from './BuildingProfile';
import './ManageAccounts.css';
// src/ManageAccounts.js
//
// Internal "Manage Accounts" page for the /maintenance hub. Back-office uses
// this single screen to:
//   • View every account (with last-login activity, building access, role)
//   • Create a new user (username/password/email/role + assigned buildings)
//   • Edit an existing user's username, password, role, or buildings
//   • Manage buildings: create new ones, edit address/notes/status, and
//     attach new contracts
//
// All writes go through /api/admin/* endpoints, which require a JWT with
// role==="maintenance". The page mirrors the Jobber Clients layout —
// stat cards on top, a filter strip, then a single sortable list.

import './InternalMobile.css';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { JobberClientPicker } from './JobberBillingFields';
import {
  FaUserPlus,
  FaBuilding,
  FaFileContract,
  FaSearch,
  FaTimes,
  FaCheck,
  FaCircle,
  FaUserCircle,
  FaPen,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaEye,
  FaEyeSlash,
  FaPlus,
  FaCalendarAlt,
  FaSyncAlt,
  FaHistory,
  FaRegCopy,
  FaExternalLinkAlt,
  FaFilePdf,
  FaTrash,
} from 'react-icons/fa';
// See note in MaintenanceHome.js — use the shared config so production
// builds never pick up the local-dev REACT_APP_API_URL=localhost:5051.
import API_BASE_URL from './config/api';
import { canSeeBillingDetails, readMaintenanceUser } from './maintenanceAccess';
import InternalUserAccessEditor from './InternalUserAccessEditor';
import { internalRoleLabel } from './internalRoleLabels';
import { contractScopeLabel, contractScopeNeedsReview } from './utils/maintenanceScope';

const ROLE_OPTIONS = [
  // Maintenance tiers — each unlocks a wider slice of the internal hub.
  // L1: In-Suite Maintenance only · L2: + Admin · L3: + Service (Jobber)
  { value: 'maintenance-L1', label: 'Employee' },
  { value: 'maintenance-L2', label: 'Manager' },
  { value: 'maintenance-L3', label: 'Admin' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'regional', label: 'Regional Manager' },
  { value: 'manager', label: 'Property Manager' },
];

// Any maintenance-* tier, or the legacy "maintenance" role.
function isMaintenanceRole(role) {
  const r = String(role || '').trim().toLowerCase();
  return r === 'maintenance' || r.startsWith('maintenance-');
}

function isL3Role(role) {
  return ['maintenance', 'maintenance-l3'].includes(String(role || '').trim().toLowerCase());
}

// L3 owns administrator accounts, internal role changes and tab visibility.
// L2 can create and manage L1/L2 accounts as well as manager accounts.
function currentAdminCanGrantMaintenance() {
  try {
    return isL3Role(readMaintenanceUser()?.role);
  } catch {
    return false;
  }
}

const C = {
  navy: '#1F3A68',
  navySoft: '#2C4E84',
  ink: '#1a2236',
  inkSoft: '#4a5468',
  muted: '#7a8499',
  line: '#E6E8EC',
  lineSoft: '#EFF2F7',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#e3f5ea',
  blue: '#2563eb',
  blueSoft: '#e8f0fe',
  amber: '#C68A1C',
  amberSoft: '#fdf3df',
  red: '#B62525',
  redSoft: '#fbe2e2',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

// ---------- helpers --------------------------------------------------------

function authHeaders() {
  const token = localStorage.getItem('managerToken') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
}

function relativeFromIso(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return d.toLocaleString();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function initialsOf(user) {
  const a = (user.first_name || '').trim();
  const b = (user.last_name || '').trim();
  if (a || b) return `${a[0] || ''}${b[0] || ''}`.toUpperCase();
  return (user.username || user.email || '?').slice(0, 2).toUpperCase();
}

// ---------- small atoms ----------------------------------------------------

function Pill({ label, value, sublabel, accent = C.navy, icon }) {
  return (
    <div className="ma-stat-card"
      style={{
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        padding: '18px 22px',
        flex: '1 1 200px',
        minWidth: 0,
        boxShadow: C.shadow,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.muted, fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {icon}
        <span>{label}</span>
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, color: accent, lineHeight: 1.1 }}>{value}</div>
      {sublabel ? (
        <div style={{ fontSize: 12.5, color: C.muted }}>{sublabel}</div>
      ) : null}
    </div>
  );
}

function RoleBadge({ role }) {
  const r = String(role || '').toLowerCase();
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11.5,
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: 999,
    textTransform: 'capitalize',
    // Keep role badges on a single line in the narrow Role column.
    whiteSpace: 'nowrap',
  };
  if (r === 'maintenance' || r.startsWith('maintenance-')) {
    const label = internalRoleLabel(role) || role;
    return (
      <span style={{ ...style, background: C.greenSoft, color: C.green }}>
        <FaCircle size={6} />
        {label}
      </span>
    );
  }
  if (r === 'corporate' || r === 'regional') {
    return <span style={{ ...style, background: C.blueSoft, color: C.navy }}><FaCircle size={6} />{role}</span>;
  }
  if (!role) {
    return <span style={{ ...style, background: '#F1F3F8', color: C.muted }}>—</span>;
  }
  return <span style={{ ...style, background: '#F1F3F8', color: C.inkSoft }}><FaCircle size={6} />{r === 'manager' ? 'Property Manager' : role}</span>;
}

function StatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11.5,
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: 999,
  };
  if (s === 'active') return <span style={{ ...base, background: C.greenSoft, color: C.green }}><FaCircle size={6} />Active</span>;
  if (s === 'pending' || s === 'invite sent') return <span style={{ ...base, background: C.amberSoft, color: C.amber }}><FaCircle size={6} />{status}</span>;
  if (s === 'inactive' || s === 'paused') return <span style={{ ...base, background: '#F1F3F8', color: C.muted }}><FaCircle size={6} />{status}</span>;
  return <span style={{ ...base, background: C.blueSoft, color: C.navy }}><FaCircle size={6} />{status || '—'}</span>;
}

function PrimaryButton({ children, onClick, type = 'button', disabled, style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? '#9CA8C3' : '#0E8C46',
        color: '#fff',
        border: 'none',
        padding: '10px 18px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 13.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, type = 'button', disabled, style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: '#fff',
        color: C.ink,
        border: `1px solid ${C.line}`,
        padding: '10px 16px',
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 13.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ---------- modals shell ---------------------------------------------------

function Modal({ open, onClose, title, subtitle, width = 640, children, footer }) {
  if (!open) return null;
  return (
    <div className="ma-modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17, 24, 39, 0.55)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '6vh 1rem 4vh',
        overflowY: 'auto',
      }}
    >
      <div className="ma-modal" role="dialog" aria-modal="true" aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          width: '100%',
          maxWidth: width,
          borderRadius: 14,
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
          fontFamily: sharedFont,
        }}
      >
        <div style={{ padding: '20px 24px 14px', borderBottom: `1px solid ${C.line}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>{title}</div>
            {subtitle ? <div style={{ marginTop: 4, color: C.muted, fontSize: 13 }}>{subtitle}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.muted, padding: 6 }}
          >
            <FaTimes />
          </button>
        </div>
        <div style={{ padding: '18px 24px 8px', maxHeight: '70vh', overflowY: 'auto' }}>{children}</div>
        {footer ? (
          <div style={{ padding: '14px 24px 18px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, hint, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: C.inkSoft, marginBottom: 6 }}>
        {label}
        {required ? <span style={{ color: C.red, marginLeft: 4 }}>*</span> : null}
      </div>
      {children}
      {hint ? <div style={{ marginTop: 4, fontSize: 11.5, color: C.muted }}>{hint}</div> : null}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: sharedFont,
  color: C.ink,
  background: '#fff',
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
};

// ---------- contract line items (shared between every modal that needs to
// show "what contracts does this building have?") ------------------------

const CONTRACT_TYPE_LABEL = {
  'hvac-maintenance': 'HVAC Preventive Maintenance',
  'dryer-vent': 'Dryer Vent Cleaning',
  'kitchen-stack': 'Kitchen Stack Cleaning',
  'mechanical-room': 'Mechanical Room',
  'valve-audit': 'Valve Audit',
  'water-savings': 'Water Savings Retrofit',
};

// Contract types where the "frequency" question doesn't make sense — e.g.
// mechanical room inspections are typically a one-off per year and we don't
// track sub-cycles. Used to hide the Frequency field in contract modals.
const NO_FREQUENCY_TYPES = new Set(['mechanical-room']);

function prettyContractType(t) {
  if (!t) return '—';
  return CONTRACT_TYPE_LABEL[String(t).toLowerCase()] || t;
}

function fmtMoney(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return null;
  try {
    return Number(v).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
  } catch {
    return `$${v}`;
  }
}

function fmtDateShort(iso) {
  if (!iso) return '';
  // Accept both "YYYY-MM-DD" and full ISO strings.
  const slice = String(iso).slice(0, 10);
  const [y, m, d] = slice.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function ContractList({
  contracts,
  dense = false,
  onAdd,
  emptyHint = 'No contracts on file yet.',
  // Per-row callbacks. When provided, ContractList renders Edit / Renew
  // icon buttons on each row and forwards the row index + the contract
  // object so the parent can locate it in `contractData`.
  onEditContract,
  onRenewContract,
  // When `showSignedContract` is true and a `building` is passed, each
  // row also exposes a SignedContractCell tied to that row's index in
  // contractData. Upload / delete fires `onSignedContractChange(nextCD)`
  // with the full updated array so the parent can patch state in place.
  building,
  showSignedContract = false,
  onSignedContractChange,
}) {
  const safe = Array.isArray(contracts) ? contracts : [];
  if (safe.length === 0) {
    return (
      <div
        style={{
          background: C.lineSoft,
          border: `1px dashed ${C.line}`,
          borderRadius: 8,
          padding: dense ? '10px 12px' : '14px 16px',
          color: C.muted,
          fontSize: 12.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>{emptyHint}</span>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              background: '#fff',
              border: `1px solid ${C.line}`,
              color: C.navy,
              padding: '5px 10px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              whiteSpace: 'nowrap',
            }}
          ><FaPlus size={9} />Add contract</button>
        ) : null}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {safe.map((c, i) => {
        const value = fmtMoney(c.value);
        const signed = fmtDateShort(c.signedDate);
        const expiry = fmtDateShort(c.expiryDate);
        const renewals = Array.isArray(c.renewals) ? c.renewals : [];
        const contractStatus = String(c.status || 'active').toLowerCase();
        const isTerminated = contractStatus === 'terminated';
        const isPaused = contractStatus === 'paused';
        const isInactive = isTerminated || isPaused;
        return (
          <div
            key={`${c.type || 'contract'}-${i}`}
            style={{
              background: '#fff',
              border: `1px solid ${C.line}`,
              borderRadius: 8,
              padding: dense ? '8px 12px' : '10px 14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span
                  style={{
                    background: isTerminated ? C.redSoft : isPaused ? C.amberSoft : C.greenSoft,
                    color: isTerminated ? C.red : isPaused ? C.amber : C.green,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 999,
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  }}
                >{contractScopeLabel(c, prettyContractType(c.type))}</span>
                {isTerminated ? (
                  <span style={{
                    background: C.redSoft,
                    color: C.red,
                    fontSize: 10.5,
                    fontWeight: 800,
                    padding: '2px 7px',
                    borderRadius: 999,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}>Terminated</span>
                ) : null}
                {isPaused ? (
                  <span style={{
                    background: C.amberSoft,
                    color: C.amber,
                    fontSize: 10.5,
                    fontWeight: 800,
                    padding: '2px 7px',
                    borderRadius: 999,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}>Paused</span>
                ) : null}
                {(c.cyclesPerYear || c.frequency) && !NO_FREQUENCY_TYPES.has(String(c.type || '').toLowerCase()) ? (
                  <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>
                    {frequencyLabel(c.cyclesPerYear || c.frequency)}
                  </span>
                ) : null}
                {contractTermLabel(c) ? (
                  <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>
                    · {contractTermLabel(c)} term
                  </span>
                ) : null}
                {value ? (
                  <span style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>{value}/yr</span>
                ) : null}
                {renewals.length > 0 ? (
                  <span
                    title={`Renewed ${renewals.length} time${renewals.length === 1 ? '' : 's'}`}
                    style={{
                      background: C.blueSoft,
                      color: C.blueInk || C.navy,
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 999,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      whiteSpace: 'nowrap',
                    }}
                  ><FaSyncAlt size={8} />Renewed {renewals.length}×</span>
                ) : null}
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {signed ? <span><FaCalendarAlt size={9} style={{ marginRight: 4, verticalAlign: -1 }} />Signed {signed}</span> : null}
                {expiry ? <span>→ Expires {expiry}</span> : null}
              </div>
              {c.notes ? (
                <div style={{ fontSize: 11.5, color: C.inkSoft, marginTop: 4 }}>
                  “{c.notes}”
                </div>
              ) : null}
              {c.type === 'dryer-vent' && <div style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 5, color: C.inkSoft }}>
                <strong style={{ color: contractScopeNeedsReview(c) ? C.amber : C.green }}>
                  {contractScopeNeedsReview(c) ? 'Scope needs verification' : 'Verified contract scope'}
                </strong>
                {c.serviceScopeNotes && <span> · {c.serviceScopeNotes}</span>}
              </div>}
              {isInactive ? (
                <div style={{
                  fontSize: 11.5,
                  color: isTerminated ? C.red : C.amber,
                  background: isTerminated ? C.redSoft : C.amberSoft,
                  borderRadius: 6,
                  padding: '6px 8px',
                  marginTop: 6,
                }}>
                  <strong>{isTerminated ? 'Termination' : 'Pause'} reason:</strong>{' '}
                  {isTerminated ? (c.terminationReason || c.statusReason || 'No reason recorded') : (c.pauseReason || c.statusReason || 'No reason recorded')}
                  {(isTerminated ? c.terminatedAt : c.pausedAt)
                    ? ` · ${fmtDateShort(isTerminated ? c.terminatedAt : c.pausedAt)}`
                    : ''}
                </div>
              ) : null}
              {renewals.length > 0 ? (
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: `1px dashed ${C.line}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, letterSpacing: '.04em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <FaHistory size={9} /> Renewal history
                  </div>
                  {renewals.map((r, ri) => {
                    const rPrev = fmtDateShort(r.previousExpiry);
                    const rNew = fmtDateShort(r.newExpiry);
                    const rSigned = fmtDateShort(r.signedDate || r.renewedAt);
                    const rValue = fmtMoney(r.value);
                    const years = r.yearsExtended != null ? Number(r.yearsExtended) : null;
                    return (
                      <div key={`renewal-${ri}`} style={{ fontSize: 11.5, color: C.inkSoft, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ color: C.muted }}>#{ri + 1}</span>
                        <span style={{ fontWeight: 600 }}>
                          {years != null ? `+${years} yr${years === 1 ? '' : 's'}` : 'Renewal'}
                        </span>
                        <span style={{ color: C.muted }}>·</span>
                        <span>{rPrev || '—'} → <strong style={{ color: C.ink }}>{rNew || '—'}</strong></span>
                        {rSigned ? (
                          <>
                            <span style={{ color: C.muted }}>·</span>
                            <span style={{ color: C.muted }}>signed {rSigned}</span>
                          </>
                        ) : null}
                        {rValue ? (
                          <>
                            <span style={{ color: C.muted }}>·</span>
                            <span style={{ color: C.muted }}>{rValue}/yr</span>
                          </>
                        ) : null}
                        {r.notes ? (
                          <span style={{ color: C.muted, fontStyle: 'italic' }}>“{r.notes}”</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            {showSignedContract && building ? (
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <SignedContractCell
                  building={building}
                  contract={c}
                  contractIndex={i}
                  size={44}
                  showRemove
                  onChange={(nextContractData) => {
                    if (Array.isArray(nextContractData) && typeof onSignedContractChange === 'function') {
                      onSignedContractChange(nextContractData);
                    }
                  }}
                />
              </div>
            ) : null}
            {(onEditContract || onRenewContract) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {onEditContract && !isInactive ? (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEditContract(i, c); }}
                    title="Pause or terminate this contract"
                    style={{
                      background: C.amberSoft,
                      border: '1px solid #edcf91',
                      color: C.amber,
                      padding: '5px 8px',
                      borderRadius: 7,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Pause / terminate
                  </button>
                ) : null}
                {onRenewContract && !isTerminated ? (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRenewContract(i, c); }}
                    title="Add a renewal — extends this contract"
                    aria-label="Renew contract"
                    style={{
                      background: '#fff',
                      border: `1px solid ${C.line}`,
                      color: C.navy,
                      padding: '5px 10px',
                      borderRadius: 7,
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      whiteSpace: 'nowrap',
                    }}
                  ><FaSyncAlt size={10} />Renew</button>
                ) : null}
                {onEditContract ? (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEditContract(i, c); }}
                    title="Edit this contract"
                    aria-label="Edit contract"
                    style={{
                      background: '#fff',
                      border: `1px solid ${C.line}`,
                      color: C.ink,
                      padding: '5px 8px',
                      borderRadius: 7,
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  ><FaPen size={10} /></button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          style={{
            alignSelf: 'flex-start',
            background: 'transparent',
            border: `1px dashed ${C.line}`,
            color: C.navy,
            padding: '6px 12px',
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 2,
          }}
        ><FaPlus size={10} />Add another contract</button>
      ) : null}
    </div>
  );
}

// ---------- building multi-select (used inside user modal) -----------------

function BuildingMultiSelect({
  allBuildings,
  selectedIds,
  onToggle,
  placeholder = 'Search active buildings…',
  // Caps the number of buildings the user can select. When `maxSelections`
  // is 1 we behave like a radio group — clicking a different building
  // swaps the selection instead of being silently ignored. When the cap is
  // higher we still allow unselecting, but block adding past the cap.
  maxSelections = Infinity,
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allBuildings;
    return allBuildings.filter((b) => {
      const hay = `${b.name} ${b.address} ${b.buildingId}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allBuildings, query]);

  const selectedSet = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);
  const atCap = (selectedIds || []).length >= maxSelections;
  const radioMode = maxSelections === 1;

  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 10, background: '#fff' }}>
      <div style={{ position: 'relative', padding: '8px 12px 6px', borderBottom: `1px solid ${C.lineSoft}` }}>
        <FaSearch style={{ position: 'absolute', left: 22, top: 16, color: C.muted, fontSize: 12 }} />
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ ...inputStyle, paddingLeft: 30 }}
        />
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '14px 16px', color: C.muted, fontSize: 13 }}>No buildings match.</div>
        ) : (
          filtered.map((b) => {
            const bid = String(b.buildingId);
            const checked = selectedSet.has(bid);
            // Disable click on UNSELECTED rows once the cap is reached
            // (radioMode handles swap explicitly so it's never disabled).
            const disabled = !checked && atCap && !radioMode;
            const indicatorIsRadio = radioMode;
            return (
              <button
                key={b.buildingId}
                type="button"
                disabled={disabled}
                title={disabled ? 'Manager accounts only allow one building. Unselect the current one first.' : undefined}
                onClick={() => {
                  if (disabled) return;
                  onToggle(bid);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  background: checked ? C.blueSoft : 'transparent',
                  border: 'none',
                  borderBottom: `1px solid ${C.lineSoft}`,
                  padding: '10px 14px',
                  textAlign: 'left',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.55 : 1,
                  fontFamily: sharedFont,
                }}
              >
                <span style={{
                  width: 18,
                  height: 18,
                  borderRadius: indicatorIsRadio ? 999 : 4,
                  border: `1.5px solid ${checked ? C.navy : C.line}`,
                  background: checked && !indicatorIsRadio ? C.navy : '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11,
                  flexShrink: 0,
                  position: 'relative',
                }}>
                  {indicatorIsRadio
                    ? (checked ? <span style={{
                        width: 8, height: 8, borderRadius: 999, background: C.navy,
                      }} /> : null)
                    : (checked ? <FaCheck /> : null)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {b.name || b.address || `Building ${b.buildingId}`}
                  </div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    {b.address || `ID ${b.buildingId}`}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.lineSoft}`, fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span>
          {(selectedIds || []).length}
          {Number.isFinite(maxSelections) ? ` / ${maxSelections}` : ''} selected
        </span>
        {radioMode ? (
          <span style={{ color: C.muted, fontStyle: 'italic' }}>One building per manager</span>
        ) : null}
      </div>
    </div>
  );
}

// ---------- User Modal -----------------------------------------------------

export function UserModal({ open, onClose, onSaved, allBuildings, editing, onAddContractForBuilding, onProfileSaved }) {
  const isEdit = !!editing;
  const canGrantMaintenance = currentAdminCanGrantMaintenance();
  const protectedAdmin = isEdit && isL3Role(editing.role) && !canGrantMaintenance;
  const internalRoleLocked = isEdit && isMaintenanceRole(editing.role) && !canGrantMaintenance;
  const profileRef = useRef(null);
  const accessRef = useRef(null);
  const [profileDirty, setProfileDirty] = useState(false);
  const [accessDirty, setAccessDirty] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);


  const [form, setForm] = useState({
    username: '',
    email: '', phone: '',
    first_name: '',
    last_name: '',
    role: 'manager',
    management_company: '',
    buildings: [],
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          username: editing.username || '',
          email: editing.email || '', phone: editing.phone || '',
          first_name: editing.first_name || '',
          last_name: editing.last_name || '',
          role: editing.role || 'manager',
          management_company: canonicalCompany(editing.management_company),
          buildings: (editing.buildings || []).map(String),
          password: '',
        });
      } else {
        setForm({
          username: '',
          email: '', phone: '',
          first_name: '',
          last_name: '',
          role: 'manager',
          management_company: '',
          buildings: [],
          password: '',
        });
      }
      setErr('');
      setAccessDirty(false);
      setAccessSaving(false);
    }
  }, [open, editing]);

  const hasProfile = isEdit && editing.role === 'manager' && editing.buildings?.length === 1;
  // Account fields come from the account record, not older building contacts.
  // A reassignment must never write those fields back to the former building.
  const canSyncProfile = hasProfile && form.role === editing.role
    && form.buildings.length === 1
    && String(form.buildings[0]) === String(editing.buildings[0]);
  const shouldSyncProfile = canSyncProfile && ['first_name', 'last_name', 'email', 'phone', 'management_company'].some(key => (
    key === 'management_company'
      ? canonicalCompany(form[key]) !== canonicalCompany(editing[key])
      : (form[key] || '') !== (editing[key] || '')
  ));
  const shouldSaveProfile = canSyncProfile && (shouldSyncProfile || profileDirty);

  // Per-role caps on how many buildings a single account is allowed to see.
  // Manager users are 1:1 with their building, so trying to assign more than
  // one is almost always a back-office mistake.
  const maxBuildings = form.role === 'manager' ? 1 : Infinity;

  // L2 can create L1/L2 teammates, but only L3 can change an existing
  // internal role or create an administrator.
  const roleOptions = useMemo(() => {
    if (canGrantMaintenance) return ROLE_OPTIONS;
    if (internalRoleLocked) return [{ value: editing.role, label: internalRoleLabel(editing.role) || editing.role }];
    return ROLE_OPTIONS.filter(option => !isL3Role(option.value));
  }, [canGrantMaintenance, internalRoleLocked, editing]);

  const toggleBuilding = (id) => {
    setForm((s) => {
      const has = s.buildings.includes(id);
      if (has) {
        return { ...s, buildings: s.buildings.filter((x) => x !== id) };
      }
      // Radio-style behavior when capped at 1: clicking a different
      // building swaps the selection in place rather than being silently
      // ignored. For higher caps we just append until we hit the limit.
      if (maxBuildings === 1) {
        return { ...s, buildings: [id] };
      }
      if (s.buildings.length >= maxBuildings) {
        return s;
      }
      return { ...s, buildings: [...s.buildings, id] };
    });
  };

  // If the user demotes an account from a multi-building role down to
  // "manager", trim the selection to a single building automatically so
  // the form can be saved without an extra step.
  useEffect(() => {
    if (maxBuildings === Infinity) return;
    setForm((s) => (
      s.buildings.length > maxBuildings
        ? { ...s, buildings: s.buildings.slice(0, maxBuildings) }
        : s
    ));
  }, [maxBuildings]);

  const submit = useCallback(async () => {
    setErr('');
    if (protectedAdmin) return setErr('An Admin must manage this account.');
    if (!isEdit) {
      if (!form.username.trim()) return setErr('Username is required.');
      if (!form.password) return setErr('Password is required for new users.');
    }
    if (shouldSaveProfile && !profileRef.current?.validate()) return setErr('Please complete the building profile fields.');
    setSaving(true);
    let profileSaved = false;
    let accessSaved = false;
    try {
      if (accessDirty) {
        if (!accessRef.current || !await accessRef.current.save()) {
          setErr('Dashboard access was not saved. Review the access section below.');
          return;
        }
        accessSaved = true;
      }
      if (shouldSaveProfile) { await profileRef.current.save(); profileSaved = true; }
      const url = isEdit
        ? `${API_BASE_URL}/api/admin/users/${editing.objectId}`
        : `${API_BASE_URL}/api/admin/users`;
      const method = isEdit ? 'PUT' : 'POST';
      const payload = { ...form, management_company:canonicalCompany(form.management_company) };
      if (shouldSyncProfile) {
        ['first_name','last_name','email','phone','management_company'].forEach(key=>delete payload[key]);
        if (editing.username?.includes('@') && (form.username === editing.username || form.username === form.email)) delete payload.username;
      }
      if (isEdit && !payload.password) delete payload.password;
      // An access-only edit must not rewrite the existing account fields.
      if (isEdit && accessSaved && !shouldSaveProfile && !Object.entries(payload).some(([key, value]) => {
        const previous = key === 'buildings' ? (editing.buildings || []).map(String)
          : key === 'management_company' ? canonicalCompany(editing.management_company)
          : editing[key] || '';
        return JSON.stringify(value) !== JSON.stringify(previous);
      })) {
        onSaved(editing, 'updated');
        onClose();
        return;
      }
      if (shouldSaveProfile) {
        Object.keys(payload).forEach(key=>{if(JSON.stringify(payload[key])===JSON.stringify(editing[key] || (key==='buildings'?[]:''))) delete payload[key];});
        if (!Object.keys(payload).length) {
          onSaved({...editing,...form,management_company:canonicalCompany(form.management_company)},'updated');
          onProfileSaved?.();onClose();return;
        }
      }
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body?.detail?.error || body?.error || `Request failed (${res.status})`;
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      onSaved(body.user, isEdit ? 'updated' : 'created');
      onProfileSaved?.();
      onClose();
    } catch (e) {
      setErr((accessSaved ? 'Dashboard access saved, but account changes failed: ' : profileSaved ? 'Profile saved, but account access changes failed: ' : '') + (e.message || 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, editing, onSaved, onClose, shouldSyncProfile, shouldSaveProfile, onProfileSaved, accessDirty, protectedAdmin]);

  const closeUserModal = () => {
    if (saving || accessSaving) return;
    if ((!profileDirty && !accessDirty) || window.confirm('Discard your unsaved changes?')) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={closeUserModal}
      width={800}
      title={isEdit ? `Edit ${editing?.username || 'user'}` : 'Add new user'}
      subtitle={isEdit ? 'Update profile, change password, or adjust building access.' : 'Create a portal account for a property manager, regional, or internal teammate.'}
      footer={(
        <>
          <SecondaryButton onClick={closeUserModal} disabled={saving || accessSaving}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || accessSaving || protectedAdmin}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
          </PrimaryButton>
        </>
      )}
    >
      {protectedAdmin && <p style={{ color: C.muted, fontSize: 13 }}>An Admin must manage this account.</p>}
      {err ? (
        <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}
      <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="First name">
          <input style={inputStyle} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        </Field>
        <Field label="Last name">
          <input style={inputStyle} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        </Field>
        <Field label="Username" required>
          <input style={inputStyle} autoComplete="off" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        </Field>
        <Field label="Email">
          <input type="email" style={inputStyle} autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value, username:form.username === form.email ? e.target.value : form.username })} />
        </Field>
        <Field label="Phone"><input type="tel" style={inputStyle} value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></Field>
        <Field label="Management company"><ManagementCompanyPicker value={form.management_company} onChange={value=>setForm({...form,management_company:value})}/></Field>
        <Field
          label={isEdit ? 'New password (leave blank to keep current)' : 'Password'}
          required={!isEdit}
          hint={isEdit ? 'Setting a value here will reset the account password immediately.' : 'They can change this from their portal later.'}
        >
          <input type="text" style={inputStyle} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        <Field label="Role" hint={internalRoleLocked ? 'An Admin can change internal roles.' : undefined}>
          <select aria-label="Role" style={inputStyle} value={form.role} disabled={internalRoleLocked} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        {open && isEdit && isMaintenanceRole(editing.role) && currentAdminCanGrantMaintenance() && <div style={{ gridColumn: '1 / -1' }}>
          <InternalUserAccessEditor key={editing.objectId} ref={accessRef} userId={editing.objectId} roleChanged={String(form.role).toLowerCase() !== String(editing.role).toLowerCase()} onDirtyChange={setAccessDirty} onSavingChange={setAccessSaving} />
        </div>}
        <div style={{ gridColumn: '1 / -1' }}>
          <Field
            label="Buildings this user can access"
            hint={
              maxBuildings === 1
                ? 'Manager accounts can access exactly one building — selecting a new one will replace the current selection.'
                : 'Only Active buildings are listed. Search by name or address.'
            }
          >
            <BuildingMultiSelect
              allBuildings={allBuildings}
              selectedIds={form.buildings}
              onToggle={toggleBuilding}
              maxSelections={maxBuildings}
            />
          </Field>
        </div>
        {form.buildings.length > 0 ? (
          <div style={{ gridColumn: '1 / -1', borderTop: `1px dashed ${C.line}`, paddingTop: 14, marginTop: 4 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 10 }}>
              Contracts on assigned buildings
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {form.buildings.map((bid) => {
                // Match either by Parse objectId or numeric buildingId — we
                // store strings on the user row but allBuildings may key
                // either way depending on which endpoint populated it.
                const match = allBuildings.find((b) => (
                  String(b.objectId) === String(bid) || String(b.buildingId) === String(bid)
                ));
                const label = match?.name || match?.address || `Building ${bid}`;
                const contracts = match?.contractData || [];
                return (
                  <div
                    key={`assigned-${bid}`}
                    style={{
                      background: C.lineSoft,
                      border: `1px solid ${C.line}`,
                      borderRadius: 10,
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      marginBottom: 10,
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, color: C.ink, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
                          <FaBuilding size={11} style={{ color: C.navy, marginRight: 6 }} />{label}
                        </div>
                        {match?.address && match.address !== label ? (
                          <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={match.address}>{match.address}</div>
                        ) : null}
                      </div>
                      {onAddContractForBuilding && match ? (
                        <button
                          type="button"
                          onClick={() => onAddContractForBuilding(match)}
                          style={{
                            background: '#fff',
                            border: `1px solid ${C.line}`,
                            color: C.navy,
                            padding: '5px 10px',
                            borderRadius: 7,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        ><FaPlus size={9} />Add contract</button>
                      ) : null}
                    </div>
                    <ContractList contracts={contracts} dense emptyHint="No contracts on this building yet." />
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      {hasProfile && <BuildingProfileEditor ref={profileRef} compact propertyOnly allowPropertyEdit={canSyncProfile} buildingId={String(editing.buildings[0])} accountUserId={editing.objectId} accountFields={shouldSyncProfile ? form : null} onDirtyChange={setProfileDirty}/>}
    </Modal>
  );
}

function RegistryBuildingSearch({onSelect}) {
  const [query,setQuery]=useState(''),[rows,setRows]=useState([]),[error,setError]=useState('');
  useEffect(()=>{
    setRows([]);setError('');if(query.trim().length<3)return;
    const controller=new AbortController();
    const timer=setTimeout(()=>fetch(`${API_BASE_URL}/api/building-profile-registry?q=${encodeURIComponent(query.trim())}`,{headers:authHeaders(),signal:controller.signal}).then(async r=>{const d=await r.json();if(!r.ok)throw new Error(d.error);return d;}).then(d=>setRows(d.buildings||[])).catch(e=>{if(e.name!=='AbortError')setError(e.message||'Registry search unavailable.');}),300);
    return()=>{clearTimeout(timer);controller.abort();};
  },[query]);
  return <div style={{marginBottom:20}}><label style={{fontWeight:600,display:'block'}}>Search address<input style={{...inputStyle,marginTop:8}} placeholder="Start typing a street address…" value={query} onChange={e=>setQuery(e.target.value)}/></label>{error&&<p role="alert">{error}</p>}{rows.map((r,i)=><button key={r.caoGuid||i} type="button" style={{display:'block',textAlign:'left',width:'100%',border:'1px solid #d6dfeb',padding:12,background:'white'}} onClick={()=>{onSelect(r);setQuery('');}}><strong>{r.streetAddress}</strong><br/>{r.registryName&&`${r.registryName} · `}{r.corporationNumber} · {r.postalCode}</button>)}</div>;
}

// ---------- Building Modal -------------------------------------------------

const REGIONS = ['Toronto', 'Mississauga', 'Etobicoke', 'North York', 'Scarborough', 'York', 'Markham', 'Vaughan', 'Other'];
const DEVICE_TYPES = ['fancoil', 'heat-pump', 'erv', 'dryer-vent', 'kitchen-stack', 'valve'];

export function BuildingModal({ open, onClose, onSaved, editing, onAddContract, onEditContract, onRenewContract, onBuildingChanged, onProfileSaved }) {
  const isEdit = !!editing;
  const profileRef = useRef(null);
  const initialSettings = useRef({});
  const [profileReady,setProfileReady] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);


  // Local mirror of contractData — gets patched in place when a per-row
  // signed-contract upload / delete fires, so the contract list reflects
  // the change without re-fetching the whole buildings list. Stays in
  // sync with the `editing` prop whenever the modal opens.
  const [contractData, setContractData] = useState(() =>
    Array.isArray(editing?.contractData) ? editing.contractData : []
  );
  useEffect(() => {
    if (open) {
      setContractData(Array.isArray(editing?.contractData) ? editing.contractData : []);
    }
  }, [open, editing]);
  const existingContracts = contractData;
  const blank = {
    name: '', address: '', caoGuid:'', registryName:'', fullAddress:'', corporationNumber:'', postalCode:'', units: '', region: 'Toronto', status: 'Active',
    manager: '', managerEmail: '', deviceType: [], photo_url: '',
    filterType: '', filterSize: '', filterQuantity: '',
    jobberClientId: '', jobberPropertyId: '', jobberClientName: '',
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const showBilling = canSeeBillingDetails((readMaintenanceUser()?.role) || '');

  useEffect(() => {
    if (open) {
      if (editing) {
        const initial = {
          name: editing.name || '',
          caoGuid:editing.caoGuid||'',registryName:editing.registryName||'',fullAddress:editing.fullAddress||'',corporationNumber:editing.corporationNumber||'',postalCode:editing.postalCode||'',
          address: editing.address || '',
          units: editing.units ?? '',
          region: editing.region || 'Toronto',
          status: editing.status || 'Active',
          manager: editing.manager || '',
          managerEmail: editing.managerEmail || '',
          deviceType: editing.deviceType || [],
          photo_url: editing.photo_url || '',
          filterType: editing.filterType || '',
          filterSize: editing.filterSize || '',
          filterQuantity: editing.filterQuantity || '',
          jobberClientId: editing.jobberClientId || '',
          jobberPropertyId: editing.jobberPropertyId || '',
          jobberClientName: editing.jobberClientName || '',
        };
        initialSettings.current=initial;
        setForm(initial);
      } else {
        setForm(blank);
      }
      setErr('');setProfileReady(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const profileLoaded = useCallback(result=>{
    setForm(f=>{
      if(f.units !== '' && f.units != null)return f;
      const units=result.units ?? '';
      // A suggested unit count is not an explicit settings edit.
      initialSettings.current={...initialSettings.current,units};
      return {...f,units};
    });
    setProfileReady(true);
  },[]);

  const toggleDevice = (d) => {
    setForm((s) => ({
      ...s,
      deviceType: s.deviceType.includes(d) ? s.deviceType.filter((x) => x !== d) : [...s.deviceType, d],
    }));
  };

  const submit = useCallback(async () => {
    setErr('');
    if (!form.name.trim() && !form.address.trim()) return setErr('Building needs a name or address.');
    if (form.units !== '' && (!Number.isInteger(Number(form.units)) || Number(form.units)<0)) return setErr('Number of units must be a whole number.');
    if (isEdit && profileDirty && !profileRef.current?.validate()) return setErr('Please complete the profile fields.');
    setSaving(true);
    let profileSaved = false;
    try {
      let profileResult;
      if (isEdit && profileDirty) {profileResult=await profileRef.current.save();profileSaved=true;}
      const payload={...form};
      if (isEdit) {
        // Profile metadata and contacts have their own permission/version checks.
        ['manager','managerEmail','caoGuid','registryName','fullAddress','corporationNumber','postalCode'].forEach(key=>delete payload[key]);
        Object.keys(payload).forEach(key=>{
          if(JSON.stringify(payload[key])===JSON.stringify(initialSettings.current[key]))delete payload[key];
        });
      }

      const url = isEdit ? `${API_BASE_URL}/api/buildings/${editing.objectId}` : `${API_BASE_URL}/api/buildings`;
      const method = isEdit ? 'PUT' : 'POST';
      let body={};
      if(!isEdit||Object.keys(payload).length){
        const res = await fetch(url, {method,headers: authHeaders(),body: JSON.stringify(payload)});
        body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
      }
      onSaved({ ...(editing || {}), ...payload, ...(profileResult?{registryName:profileResult.profile.name,fullAddress:profileResult.profile.address,corporationNumber:profileResult.profile.corporationNumber,postalCode:profileResult.profile.postalCode,manager:profileResult.profile.manager,managerEmail:profileResult.profile.managerEmail,managementCompany:profileResult.profile.managementCompany}:{}), objectId: editing?.objectId || body.objectId }, isEdit ? 'updated' : 'created');
      onProfileSaved?.();onClose();
    } catch (e) {
      setErr((profileSaved ? 'Profile saved, but building settings could not be saved: ' : '')+(e.message || 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, editing, onSaved, onClose, onProfileSaved, profileDirty]);

  const propertyFields = (<div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="Dashboard label (street address)">
          <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Street address">
          <input style={inputStyle} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value, caoGuid:'' })} />
        </Field>
        <Field label="Number of units">
          <input style={inputStyle} type="number" min="0" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} />
        </Field>
        <Field label="Region">
          <select style={inputStyle} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select style={inputStyle} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Active</option>
            <option>Pending</option>
            <option>Inactive</option>
          </select>
        </Field>
        {!isEdit&&<>
        <Field label="Primary manager">
          <input style={inputStyle} value={form.manager} onChange={(e) => setForm({ ...form, manager: e.target.value })} />
        </Field>
        <Field label="Manager email">
          <input type="email" style={inputStyle} value={form.managerEmail} onChange={(e) => setForm({ ...form, managerEmail: e.target.value })} />
        </Field>
        </>}
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Building photo URL" hint="Optional. Link to a photo of the building.">
            <input style={inputStyle} value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" />
          </Field>
          {form.photo_url ? (
            <img
              src={form.photo_url}
              alt="Building preview"
              style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, border: `1px solid ${C.line}`, objectFit: 'cover' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              onLoad={(e) => { e.currentTarget.style.display = 'block'; }}
            />
          ) : null}
        </div>
      </div>);

  return (
    <Modal
      open={open}
      onClose={()=>{if(!profileDirty||window.confirm('Discard your unsaved profile changes?'))onClose();}}
      width={800}
      title={isEdit ? `Edit ${editing?.name || editing?.address || 'building'}` : 'Add new building'}
      subtitle={isEdit ? 'Manage property details, contacts, and building settings.' : 'Create a building record. Contracts can be added once it exists.'}
      footer={(
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving||(isEdit&&!profileReady)}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create building'}
          </PrimaryButton>
        </>
      )}
    >
      <div className="building-profile bp-compact bp-building-editor">
      {!isEdit && <RegistryBuildingSearch onSelect={r=>setForm(f=>({...f,...r,name:r.streetAddress,address:r.streetAddress,units:r.units??''}))}/>}
      {!isEdit && form.caoGuid && <div style={{padding:14,background:'#eef4fb',marginBottom:18,borderRadius:8}}><strong>{form.registryName||form.name}</strong><br/>{form.fullAddress}<br/>{form.corporationNumber} · {form.postalCode}</div>}
      {err ? (
        <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}
      {isEdit ? <BuildingProfileEditor ref={profileRef} compact buildingId={String(editing?.buildingId || editing?.objectId || '')} onLoaded={profileLoaded} onDirtyChange={setProfileDirty} onSubmitSave={submit} propertyExtras={<div className="bp-property-settings">{propertyFields}</div>}/> : <section className="bp-section"><div className="bp-section-title"><div><h2>Property</h2><p>Building details and settings.</p></div></div>{propertyFields}</section>}
      <section className="bp-section"><div className="bp-section-title"><div><h2>Equipment &amp; contracts</h2><p>Service details for this building.</p></div></div>
      <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        {/* Filter info — site-specific equipment metadata we surface in
            calendar pop-ups, scheduling sheets, and the Home appointments
            list. Leave blank for now; back-office fills these in over time. */}
        <div style={{ gridColumn: '1 / -1', borderTop: `1px dashed ${C.line}`, paddingTop: 14, marginTop: 4 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 10 }}>Filter info</div>
        </div>
        <Field label="Filter type" hint="e.g. Pleated MERV-8, Fiberglass">
          <input style={inputStyle} value={form.filterType} onChange={(e) => setForm({ ...form, filterType: e.target.value })} />
        </Field>
        <Field label="Filter size" hint="e.g. 16x20x1">
          <input style={inputStyle} value={form.filterSize} onChange={(e) => setForm({ ...form, filterSize: e.target.value })} />
        </Field>
        <Field label="Filters per unit (qty)" hint="Optional, e.g. 1 or 2">
          <input style={inputStyle} value={form.filterQuantity} onChange={(e) => setForm({ ...form, filterQuantity: e.target.value })} />
        </Field>
        {showBilling && (
          <>
            <div style={{ gridColumn: '1 / -1', borderTop: `1px dashed ${C.line}`, paddingTop: 14, marginTop: 4 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 10 }}>Jobber</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Jobber client" hint="Links this building to a Jobber client + property for scheduling.">
                <JobberClientPicker
                  clientName={form.jobberClientName}
                  onPick={(c) => setForm((s) => ({ ...s, jobberClientId: c.id, jobberPropertyId: c.propertyId || '', jobberClientName: c.name || '' }))}
                  onClear={() => setForm((s) => ({ ...s, jobberClientId: '', jobberPropertyId: '', jobberClientName: '' }))}
                />
              </Field>
            </div>
          </>
        )}
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Equipment types on site">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {DEVICE_TYPES.map((d) => {
                const on = form.deviceType.includes(d);
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={() => toggleDevice(d)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1px solid ${on ? C.navy : C.line}`,
                      background: on ? C.navy : '#fff',
                      color: on ? '#fff' : C.inkSoft,
                      fontSize: 12.5,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >{d}</button>
                );
              })}
            </div>
          </Field>
        </div>
        {isEdit ? (
          <div style={{ gridColumn: '1 / -1', borderTop: `1px dashed ${C.line}`, paddingTop: 14, marginTop: 4 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
              gap: 12,
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, letterSpacing: '.05em', textTransform: 'uppercase' }}>
                Contracts on file
                <span style={{ marginLeft: 8, background: C.lineSoft, color: C.inkSoft, padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>
                  {existingContracts.length}
                </span>
              </div>
              {onAddContract ? (
                <button
                  type="button"
                  onClick={() => onAddContract(editing)}
                  style={{
                    background: '#fff',
                    border: `1px solid ${C.line}`,
                    color: C.navy,
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                ><FaPlus size={10} />Add contract</button>
              ) : null}
            </div>
            <ContractList
              contracts={existingContracts}
              dense
              emptyHint="No contracts on this building yet."
              onEditContract={onEditContract ? (idx, c) => onEditContract(editing, idx, c) : undefined}
              onRenewContract={onRenewContract ? (idx, c) => onRenewContract(editing, idx, c) : undefined}
              // Each contract row gets its own signed-contract slot —
              // upload / view / remove flows live alongside the metadata
              // for that specific contract.
              building={editing}
              showSignedContract
              onSignedContractChange={(nextContractData) => {
                if (Array.isArray(nextContractData)) {
                  setContractData(nextContractData);
                  if (editing?.objectId && typeof onBuildingChanged === 'function') {
                    onBuildingChanged(editing.objectId, { contractData: nextContractData });
                  }
                }
              }}
            />
          </div>
        ) : null}
      </div>
      </section>
      </div>
    </Modal>
  );
}

// ---------- Contract Modal -------------------------------------------------

const CONTRACT_TYPES = [
  { value: 'hvac-maintenance', label: 'HVAC Preventive Maintenance' },
  { value: 'dryer-vent', label: 'Dryer Vent Cleaning' },
  { value: 'kitchen-stack', label: 'Kitchen Stack Cleaning' },
  { value: 'mechanical-room', label: 'Mechanical Room' },
  { value: 'valve-audit', label: 'Valve Audit' },
  { value: 'water-savings', label: 'Water Savings Retrofit' },
];

// Frequency picker — supports both "N times per year" (cyclesPerYear ≥ 1)
// and "1 time every N years" (fractional cyclesPerYear). Fractional values
// flow through to the schedule calc as `365 / cyclesPerYear` days between
// cycles, so 0.5 = every 2 years, 1/3 = every 3 years, etc.
const FREQUENCY_OPTIONS = [
  { value: 4, label: '4x per year (quarterly)' },
  { value: 3, label: '3x per year' },
  { value: 2, label: '2x per year' },
  { value: 1, label: '1x per year' },
  { value: 0.5, label: '1x every 2 years' },
  { value: 1 / 3, label: '1x every 3 years' },
  { value: 0.25, label: '1x every 4 years' },
  { value: 0.2, label: '1x every 5 years' },
];

const CONTRACT_TERM_OPTIONS = [
  { value: 6, label: '6 months' },
  { value: 12, label: '1 year' },
  { value: 24, label: '2 years' },
  { value: 36, label: '3 years' },
  { value: 48, label: '4 years' },
  { value: 60, label: '5 years' },
];

function addMonthsToISO(iso, months) {
  if (!iso) return '';
  const [year, month, day] = String(iso).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '';
  const targetMonth = month - 1 + Number(months || 0);
  const targetYear = year + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  return `${targetYear}-${String(normalizedMonth + 1).padStart(2, '0')}-${String(Math.min(day, lastDay)).padStart(2, '0')}`;
}

function monthsBetweenISO(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const [startYear, startMonth, startDay] = String(startIso).slice(0, 10).split('-').map(Number);
  const [endYear, endMonth, endDay] = String(endIso).slice(0, 10).split('-').map(Number);
  if (!startYear || !startMonth || !startDay || !endYear || !endMonth || !endDay) return 0;
  const months = ((endYear - startYear) * 12) + (endMonth - startMonth);
  return Math.max(0, months - (endDay < startDay ? 1 : 0));
}

function contractTermMonths(contract) {
  const explicit = Number(contract?.termLengthMonths || 0);
  return explicit > 0 ? explicit : monthsBetweenISO(contract?.signedDate, contract?.expiryDate);
}

function contractTermLabel(contract) {
  const months = contractTermMonths(contract);
  if (!months) return '';
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} year${years === 1 ? '' : 's'}`;
  }
  return `${months} months`;
}

// Human-readable label for any cyclesPerYear / frequency value, including
// the fractional "1x every N years" cases. Matches with a tolerance so the
// JS float representation of 1/3 still renders as "1× every 3 years".
function frequencyLabel(raw) {
  const v = Number(raw);
  if (!Number.isFinite(v) || v <= 0) return '';
  if (v >= 1) return `${Math.round(v)}× per year`;
  const years = Math.round(1 / v);
  if (!years || !Number.isFinite(years)) return '';
  return years === 1 ? '1× per year' : `1× every ${years} years`;
}

function ContractModal({ open, onClose, onSaved, buildings, defaultBuildingObjectId = '' }) {
  const [buildingObjectId, setBuildingObjectId] = useState('');
  const [type, setType] = useState(CONTRACT_TYPES[0].value);
  const [signedDate, setSignedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [termLengthMonths, setTermLengthMonths] = useState(12);
  const [frequency, setFrequency] = useState(1);
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setBuildingObjectId(defaultBuildingObjectId || '');
      setType(CONTRACT_TYPES[0].value);
      setSignedDate(new Date().toISOString().slice(0, 10));
      const e = new Date();
      e.setFullYear(e.getFullYear() + 1);
      setExpiryDate(e.toISOString().slice(0, 10));
      setTermLengthMonths(12);
      setFrequency(1);
      setValue('');
      setNotes('');
      setErr('');
    }
  }, [open, defaultBuildingObjectId]);

  const selectedBuilding = useMemo(
    () => buildings.find((b) => b.objectId === buildingObjectId) || null,
    [buildings, buildingObjectId]
  );
  const existingContracts = useMemo(() => {
    if (!selectedBuilding) return [];
    return Array.isArray(selectedBuilding.contractData) ? selectedBuilding.contractData : [];
  }, [selectedBuilding]);

  const submit = useCallback(async () => {
    setErr('');
    if (!buildingObjectId) return setErr('Pick a building.');
    setSaving(true);
    try {
      const target = buildings.find((b) => b.objectId === buildingObjectId);
      if (!target) throw new Error('Building lookup failed.');
      const existingData = Array.isArray(target.contractData) ? target.contractData : [];
      const existingTypes = Array.isArray(target.contracts) ? target.contracts : [];
      const newEntry = {
        type, signedDate, expiryDate, termLengthMonths,
        // Persist both keys so the backend (`cyclesPerYear`) and the
        // legacy ManageAccounts contracts list (`frequency`) stay in sync.
        cyclesPerYear: frequency,
        frequency,
        value: value ? Number(value) : null,
        notes,
        addedAt: new Date().toISOString(),
      };
      const payload = {
        contractData: [...existingData, newEntry],
        contracts: existingTypes.includes(type) ? existingTypes : [...existingTypes, type],
      };
      const res = await fetch(`${API_BASE_URL}/api/buildings/${buildingObjectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
      onSaved(target, newEntry);
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [buildingObjectId, type, signedDate, expiryDate, termLengthMonths, frequency, value, notes, buildings, onSaved, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={600}
      title="Add contract"
      subtitle="Attach a contract record to an existing building. Used by Schedule + Reports."
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Add contract'}</PrimaryButton>
        </>
      }
    >
      {err ? (
        <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}
      <Field label="Building" required>
        <select style={inputStyle} value={buildingObjectId} onChange={(e) => setBuildingObjectId(e.target.value)}>
          <option value="">— Select a building —</option>
          {buildings.map((b) => (
            <option key={b.objectId} value={b.objectId}>
              {(b.name || b.address || `Building ${b.buildingId}`)}{b.status && b.status !== 'Active' ? ` · ${b.status}` : ''}
            </option>
          ))}
        </select>
      </Field>
      {selectedBuilding ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.muted,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span>Existing contracts on this building</span>
            <span style={{ background: C.lineSoft, color: C.inkSoft, padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>
              {existingContracts.length}
            </span>
          </div>
          <ContractList contracts={existingContracts} dense emptyHint="No contracts yet — this will be the first." />
        </div>
      ) : null}
      <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="Contract type" required>
          <select style={inputStyle} value={type} onChange={(e) => setType(e.target.value)}>
            {CONTRACT_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        {!NO_FREQUENCY_TYPES.has(type) ? (
          <Field label="Frequency">
            <select style={inputStyle} value={frequency} onChange={(e) => setFrequency(Number(e.target.value))}>
              {FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>
        ) : (
          <div /> /* keep grid alignment without showing the frequency picker */
        )}
        <Field label="Signed date">
          <input
            type="date"
            style={inputStyle}
            value={signedDate}
            onChange={(e) => {
              const nextSignedDate = e.target.value;
              setSignedDate(nextSignedDate);
              setExpiryDate(addMonthsToISO(nextSignedDate, termLengthMonths));
            }}
          />
        </Field>
        <Field label="Term length" required>
          <select
            style={inputStyle}
            value={termLengthMonths}
            onChange={(e) => {
              const nextTerm = Number(e.target.value);
              setTermLengthMonths(nextTerm);
              setExpiryDate(addMonthsToISO(signedDate, nextTerm));
            }}
          >
            {CONTRACT_TERM_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Expiry date">
          <input
            type="date"
            style={inputStyle}
            value={expiryDate}
            onChange={(e) => {
              const nextExpiry = e.target.value;
              setExpiryDate(nextExpiry);
              const inferred = monthsBetweenISO(signedDate, nextExpiry);
              if (CONTRACT_TERM_OPTIONS.some((option) => option.value === inferred)) {
                setTermLengthMonths(inferred);
              }
            }}
          />
        </Field>
        <Field label="Annual value (CAD)">
          <input type="number" min="0" step="100" style={inputStyle} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

// ---------- Contract EDIT Modal --------------------------------------------
// Lets the user fix mistakes or correct a contract record in place. Renewals
// must use the dedicated renew modal; editing here is for things like a
// typo'd signed date, a wrong contract type, or an updated annual value.

function ContractEditModal({ open, onClose, building, contract, contractIndex, onSaved }) {
  const [type, setType] = useState(CONTRACT_TYPES[0].value);
  const [signedDate, setSignedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [termLengthMonths, setTermLengthMonths] = useState(12);
  const [frequency, setFrequency] = useState(1);
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [contractStatus, setContractStatus] = useState('active');
  const [statusReason, setStatusReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && contract) {
      setType(contract.type || CONTRACT_TYPES[0].value);
      setSignedDate((contract.signedDate || '').slice(0, 10));
      setExpiryDate((contract.expiryDate || '').slice(0, 10));
      setTermLengthMonths(contractTermMonths(contract) || 12);
      setFrequency(Number(contract.cyclesPerYear || contract.frequency || 1));
      setValue(contract.value != null && contract.value !== '' ? String(contract.value) : '');
      setNotes(contract.notes || '');
      setContractStatus(String(contract.status || 'active').toLowerCase());
      setStatusReason(contract.status === 'paused'
        ? (contract.pauseReason || contract.statusReason || '')
        : (contract.terminationReason || contract.statusReason || ''));
      setErr('');
    }
  }, [open, contract]);

  const renewals = Array.isArray(contract?.renewals) ? contract.renewals : [];

  const submit = useCallback(async () => {
    if (!building?.objectId) return setErr('Building lookup failed.');
    if (contractIndex == null) return setErr('Contract index missing.');
    if (['paused', 'terminated'].includes(contractStatus) && !statusReason.trim()) {
      return setErr(`A ${contractStatus === 'paused' ? 'pause' : 'termination'} reason is required.`);
    }
    setErr('');
    setSaving(true);
    try {
      const existing = Array.isArray(building.contractData) ? [...building.contractData] : [];
      const updated = {
        ...(existing[contractIndex] || {}),
        type,
        signedDate,
        expiryDate,
        termLengthMonths,
        // Persist both keys so the backend (`cyclesPerYear`) and the
        // legacy ManageAccounts contracts list (`frequency`) stay in sync.
        cyclesPerYear: frequency,
        frequency,
        value: value === '' ? null : Number(value),
        notes,
        status: contractStatus,
        statusReason: ['paused', 'terminated'].includes(contractStatus) ? statusReason.trim() : '',
        pauseReason: contractStatus === 'paused' ? statusReason.trim() : '',
        pausedAt: contractStatus === 'paused'
          ? ((existing[contractIndex] || {}).pausedAt || new Date().toISOString())
          : null,
        terminationReason: contractStatus === 'terminated' ? statusReason.trim() : '',
        terminatedAt: contractStatus === 'terminated'
          ? ((existing[contractIndex] || {}).terminatedAt || new Date().toISOString())
          : null,
        // Bookkeeping — when this row was last manually edited (separate
        // from a renewal event, which appends to `renewals`).
        editedAt: new Date().toISOString(),
      };
      existing[contractIndex] = updated;
      // Rebuild the lightweight `contracts` (list of types) array so it
      // stays in sync with whatever types are currently present.
      const typesSet = new Set();
      existing.forEach((c) => {
        if (c?.type && String(c.status || 'active').toLowerCase() !== 'terminated') {
          typesSet.add(c.type);
        }
      });
      const payload = { contractData: existing, contracts: Array.from(typesSet) };
      const res = await fetch(`${API_BASE_URL}/api/buildings/${building.objectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
      onSaved({ ...building, contractData: existing, contracts: Array.from(typesSet) }, updated, 'edited');
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [building, contractIndex, type, signedDate, expiryDate, termLengthMonths, frequency, value, notes, contractStatus, statusReason, onSaved, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={620}
      title="Edit contract"
      subtitle={building ? `${building.name || building.address || 'Building'} · ${contractScopeLabel(contract, prettyContractType(contract?.type))}` : ''}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</PrimaryButton>
        </>
      }
    >
      {err ? (
        <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}
      {renewals.length > 0 ? (
        <div style={{
          background: C.blueSoft,
          color: C.blueInk || C.navy,
          border: `1px solid ${C.line}`,
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 12.5,
          marginBottom: 12,
        }}>
          This contract has been renewed <strong>{renewals.length}</strong> time{renewals.length === 1 ? '' : 's'}. Editing the expiry here will overwrite the renewed expiry — use the <em>Renew</em> button on the row instead if you want to log a new renewal.
        </div>
      ) : null}
      <Field label="Contract status" required>
        <select
          style={{
            ...inputStyle,
            borderColor: contractStatus === 'terminated' ? '#ef9a9a' : inputStyle.borderColor,
            color: contractStatus === 'terminated' ? C.red : C.ink,
            fontWeight: 650,
          }}
          value={contractStatus}
          onChange={(e) => {
            const nextStatus = e.target.value;
            setContractStatus(nextStatus);
            setStatusReason(nextStatus === 'paused'
              ? (contract?.pauseReason || contract?.statusReason || '')
              : nextStatus === 'terminated'
                ? (contract?.terminationReason || contract?.statusReason || '')
                : '');
          }}
        >
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="terminated">Terminated</option>
        </select>
      </Field>
      {['paused', 'terminated'].includes(contractStatus) ? (
        <div style={{
          background: contractStatus === 'terminated' ? C.redSoft : C.amberSoft,
          border: `1px solid ${contractStatus === 'terminated' ? '#f2b8bd' : '#edcf91'}`,
          borderRadius: 9,
          padding: '11px 12px',
          marginBottom: 14,
        }}>
          <div style={{ color: contractStatus === 'terminated' ? C.red : C.amber, fontSize: 12.5, fontWeight: 750, marginBottom: 8 }}>
            {contractStatus === 'terminated'
              ? 'Terminated contracts are retained for history but immediately removed from scheduling.'
              : 'Paused contracts are retained for history and removed from scheduling until reactivated.'}
          </div>
          <Field
            label={contractStatus === 'terminated' ? 'Termination reason' : 'Pause reason'}
            required
            hint={contractStatus === 'terminated'
              ? 'Record why Hudson is no longer responsible for this contract.'
              : 'Record why scheduling is temporarily paused.'}
          >
            <textarea
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', background: '#fff' }}
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder={contractStatus === 'terminated'
                ? 'e.g. Building changed vendors effective July 2026'
                : 'e.g. Building requested no visits until board approval'}
            />
          </Field>
        </div>
      ) : null}
      <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="Contract type" required>
          <select style={inputStyle} value={type} onChange={(e) => setType(e.target.value)}>
            {CONTRACT_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        {!NO_FREQUENCY_TYPES.has(type) ? (
          <Field label="Frequency">
            <select style={inputStyle} value={frequency} onChange={(e) => setFrequency(Number(e.target.value))}>
              {FREQUENCY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>
        ) : (
          <div />
        )}
        <Field label="Signed date">
          <input
            type="date"
            style={inputStyle}
            value={signedDate}
            onChange={(e) => {
              const nextSignedDate = e.target.value;
              setSignedDate(nextSignedDate);
              setExpiryDate(addMonthsToISO(nextSignedDate, termLengthMonths));
            }}
          />
        </Field>
        <Field label="Term length" required>
          <select
            style={inputStyle}
            value={termLengthMonths}
            onChange={(e) => {
              const nextTerm = Number(e.target.value);
              setTermLengthMonths(nextTerm);
              setExpiryDate(addMonthsToISO(signedDate, nextTerm));
            }}
          >
            {CONTRACT_TERM_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Expiry date">
          <input
            type="date"
            style={inputStyle}
            value={expiryDate}
            onChange={(e) => {
              const nextExpiry = e.target.value;
              setExpiryDate(nextExpiry);
              const inferred = monthsBetweenISO(signedDate, nextExpiry);
              if (CONTRACT_TERM_OPTIONS.some((option) => option.value === inferred)) {
                setTermLengthMonths(inferred);
              }
            }}
          />
        </Field>
        <Field label="Annual value (CAD)">
          <input type="number" min="0" step="100" style={inputStyle} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

// ---------- Contract RENEW Modal -------------------------------------------
// Logs a renewal event without rewriting history. The current contract gets
// a new `expiryDate`, and a renewal record is appended to `contract.renewals`
// so we can audit when/why the term was extended (and by how much).

const RENEWAL_YEARS_OPTIONS = [
  { value: 1, label: '1 year' },
  { value: 2, label: '2 years' },
  { value: 3, label: '3 years' },
  { value: 4, label: '4 years' },
  { value: 5, label: '5 years' },
];

function addYearsToISO(iso, years) {
  if (!iso) return '';
  const slice = String(iso).slice(0, 10);
  const [y, m, d] = slice.split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d);
  dt.setFullYear(dt.getFullYear() + Number(years || 0));
  // Format back to YYYY-MM-DD (local, no TZ shenanigans).
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function ContractRenewModal({ open, onClose, building, contract, contractIndex, onSaved }) {
  const [years, setYears] = useState(1);
  // The renewal can be either "extend the current expiry by N years" (the
  // common case) or "set an explicit new expiry" (override). We default to
  // extend; the user can swap to custom via the toggle.
  const [mode, setMode] = useState('extend'); // 'extend' | 'custom'
  const [customExpiry, setCustomExpiry] = useState('');
  const [signedDate, setSignedDate] = useState('');
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open && contract) {
      setYears(1);
      setMode('extend');
      setSignedDate(todayISO());
      // Carry over the current annual value as a default — renewals often
      // keep the same price unless changed.
      setValue(contract.value != null && contract.value !== '' ? String(contract.value) : '');
      setNotes('');
      setErr('');
      // Default custom expiry = current expiry + 1 year so the picker
      // doesn't start empty.
      setCustomExpiry(addYearsToISO(contract.expiryDate, 1));
    }
  }, [open, contract]);

  const currentExpiry = contract?.expiryDate || '';
  const newExpiry = useMemo(() => {
    if (mode === 'custom') return customExpiry;
    return addYearsToISO(currentExpiry || todayISO(), years);
  }, [mode, customExpiry, currentExpiry, years]);

  const submit = useCallback(async () => {
    if (!building?.objectId) return setErr('Building lookup failed.');
    if (contractIndex == null) return setErr('Contract index missing.');
    if (!newExpiry) return setErr('Pick a new expiry date.');
    if (currentExpiry && newExpiry <= String(currentExpiry).slice(0, 10)) {
      return setErr('The new expiry must be later than the current expiry.');
    }
    setErr('');
    setSaving(true);
    try {
      const existing = Array.isArray(building.contractData) ? [...building.contractData] : [];
      const target = existing[contractIndex] || {};
      const renewalEntry = {
        previousExpiry: currentExpiry || null,
        newExpiry,
        yearsExtended: mode === 'extend' ? Number(years) : null,
        signedDate,
        value: value === '' ? null : Number(value),
        notes,
        renewedAt: new Date().toISOString(),
      };
      const updated = {
        ...target,
        // Push the live expiry forward — this is what the rest of the app
        // reads for "is this contract still active?".
        expiryDate: newExpiry,
        // Keep the explicit term synchronized with the renewed expiry so
        // schedule-cycle obligations and lifetime value extend with it.
        termLengthMonths: monthsBetweenISO(target.signedDate, newExpiry)
          || (contractTermMonths(target) + (mode === 'extend' ? Number(years) * 12 : 0)),
        // If a new annual value was provided, also update the live value
        // (the previous one is preserved on the renewal record).
        value: value === '' ? target.value : Number(value),
        renewals: [...(Array.isArray(target.renewals) ? target.renewals : []), renewalEntry],
        lastRenewedAt: renewalEntry.renewedAt,
      };
      existing[contractIndex] = updated;
      const typesSet = new Set();
      existing.forEach((c) => { if (c?.type) typesSet.add(c.type); });
      const payload = { contractData: existing, contracts: Array.from(typesSet) };
      const res = await fetch(`${API_BASE_URL}/api/buildings/${building.objectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
      onSaved({ ...building, contractData: existing, contracts: Array.from(typesSet) }, updated, 'renewed');
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [building, contractIndex, currentExpiry, newExpiry, mode, years, signedDate, value, notes, onSaved, onClose]);

  const contractLabel = contract ? contractScopeLabel(contract, prettyContractType(contract.type)) : '';
  const currentExpiryLabel = fmtDateShort(currentExpiry);
  const newExpiryLabel = fmtDateShort(newExpiry);

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={620}
      title="Renew contract"
      subtitle={building ? `${building.name || building.address || 'Building'} · ${contractLabel}` : ''}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || !newExpiry}>
            {saving ? 'Saving…' : 'Save renewal'}
          </PrimaryButton>
        </>
      }
    >
      {err ? (
        <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}
      {contract ? (
        <div style={{
          background: C.lineSoft,
          border: `1px solid ${C.line}`,
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 14,
          fontSize: 12.5,
          color: C.inkSoft,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
        }}>
          <span style={{
            background: C.greenSoft,
            color: C.green,
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 999,
          }}>{contractLabel}</span>
          <span><strong>Current expiry:</strong> {currentExpiryLabel || '—'}</span>
          <span>→ <strong style={{ color: C.green }}>New expiry: {newExpiryLabel || '—'}</strong></span>
          {Array.isArray(contract.renewals) && contract.renewals.length > 0 ? (
            <span style={{
              background: C.blueSoft,
              color: C.blueInk || C.navy,
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 999,
            }}>Renewed {contract.renewals.length}× already</span>
          ) : null}
        </div>
      ) : null}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 14,
        background: '#fff',
        border: `1px solid ${C.line}`,
        borderRadius: 999,
        padding: 4,
        alignSelf: 'flex-start',
        width: 'fit-content',
      }}>
        <button
          type="button"
          onClick={() => setMode('extend')}
          style={{
            background: mode === 'extend' ? C.navy : 'transparent',
            color: mode === 'extend' ? '#fff' : C.inkSoft,
            border: 'none',
            padding: '6px 14px',
            borderRadius: 999,
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >Extend by years</button>
        <button
          type="button"
          onClick={() => setMode('custom')}
          style={{
            background: mode === 'custom' ? C.navy : 'transparent',
            color: mode === 'custom' ? '#fff' : C.inkSoft,
            border: 'none',
            padding: '6px 14px',
            borderRadius: 999,
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >Custom expiry date</button>
      </div>
      <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        {mode === 'extend' ? (
          <Field label="Extend by" required>
            <select style={inputStyle} value={years} onChange={(e) => setYears(Number(e.target.value))}>
              {RENEWAL_YEARS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="New expiry date" required>
            <input
              type="date"
              style={inputStyle}
              value={customExpiry}
              min={currentExpiry ? String(currentExpiry).slice(0, 10) : undefined}
              onChange={(e) => setCustomExpiry(e.target.value)}
            />
          </Field>
        )}
        <Field label="Renewal signed date">
          <input type="date" style={inputStyle} value={signedDate} onChange={(e) => setSignedDate(e.target.value)} />
        </Field>
        <Field label="New annual value (CAD)" hint="Leave blank to keep the existing value.">
          <input type="number" min="0" step="100" style={inputStyle} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional — price changes, scope adjustments, etc." />
      </Field>
    </Modal>
  );
}

// ---------- main component -------------------------------------------------

// Allow-list for the ?tab= query param — anything outside this set
// falls back to "users". Defined at module scope so the URL → state
// reader doesn't reallocate it on every render.
const VALID_TABS = new Set(['users', 'buildings']);

export default function ManageAccounts() {

  // Persist the active tab in the URL (`?tab=buildings|users`) so a
  // refresh — or a shared link — keeps the operator on the same view.
  // Using react-router's useSearchParams keeps history entries clean
  // (no full reload) and matches the rest of the dashboard.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = VALID_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : 'users';
  const setTab = useCallback((next) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'users') {
          // Default value — drop the param so the URL stays clean for
          // the most common entry point.
          params.delete('tab');
        } else {
          params.set('tab', next);
        }
        return params;
      },
      { replace: true } // No history spam for tab toggles.
    );
  }, [setSearchParams]);
  const [users, setUsers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [activeBuildings, setActiveBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [userModal, setUserModal] = useState({ open: false, editing: null });
  const [bldgModal, setBldgModal] = useState({ open: false, editing: null });
  const [contractModal, setContractModal] = useState({ open: false, defaultBuildingObjectId: '' });
  // Edit-contract + renew-contract modal state. We carry the FULL building
  // object so the modal can write back to contractData[index] without
  // re-querying — and a `returnToBuilding` flag so we can re-open the
  // BuildingModal after save (preserving the user's editing context).
  const [contractEditModal, setContractEditModal] = useState({ open: false, building: null, contract: null, contractIndex: null, returnToBuilding: false });
  const [contractRenewModal, setContractRenewModal] = useState({ open: false, building: null, contract: null, contractIndex: null, returnToBuilding: false });

  const refreshAttemptedRef = React.useRef(false);

  const tryRefreshToken = useCallback(async () => {
    const token = localStorage.getItem('managerToken');
    if (!token) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/units/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (!data?.token) return false;
      localStorage.setItem('managerToken', data.token);
      if (data.user) localStorage.setItem('managerUser', JSON.stringify(data.user));
      return true;
    } catch {
      return false;
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Hit each endpoint as a raw Response so we can distinguish 403 from
      // an empty-but-valid response. Old code unconditionally JSON-parsed,
      // which lost the status code.
      const fetchJson = async (url, opts) => {
        const r = await fetch(url, opts);
        const body = await r.json().catch(() => ({}));
        return { status: r.status, body };
      };
      let [u, b, a] = await Promise.all([
        fetchJson(`${API_BASE_URL}/api/admin/users`, { headers: authHeaders() }),
        fetchJson(`${API_BASE_URL}/api/buildings`, { headers: authHeaders() }),
        fetchJson(`${API_BASE_URL}/api/admin/active-buildings`, { headers: authHeaders() }),
      ]);

      // If we hit the legacy-token race (403) try a one-shot token refresh
      // then retry the failing admin calls. This is the bandaid for stale
      // sessions that have a valid JWT but no `role` claim.
      const adminBlocked = u.status === 403 || a.status === 403;
      if (adminBlocked && !refreshAttemptedRef.current) {
        refreshAttemptedRef.current = true;
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          [u, a] = await Promise.all([
            fetchJson(`${API_BASE_URL}/api/admin/users`, { headers: authHeaders() }),
            fetchJson(`${API_BASE_URL}/api/admin/active-buildings`, { headers: authHeaders() }),
          ]);
        }
      }

      const normalizedUsers = (u.body?.users || []).map(user=>({...user, management_company:canonicalCompany(user.management_company), buildingDetails:(user.buildingDetails||[]).map(b=>({...b,managementCompany:canonicalCompany(b.managementCompany || user.management_company)}))}));
      if (Array.isArray(u.body?.users)) setUsers(normalizedUsers);
      if (Array.isArray(b.body?.buildings)) setBuildings(b.body.buildings.map(building=>{
        const linked = normalizedUsers.filter(user=>(user.buildings||[]).some(id=>[String(building.buildingId),String(building.objectId)].includes(String(id))));
        const primary = linked.find(user=>user.role==='manager' && user.email?.toLowerCase()===building.managerEmail?.toLowerCase()) || linked.find(user=>user.role==='manager');
        const companies = [...new Set(linked.map(user=>user.management_company).filter(Boolean))];
        return {...building,managementCompany:canonicalCompany(building.managementCompany || primary?.management_company || (companies.length===1?companies[0]:''))};
      }));
      if (Array.isArray(a.body?.buildings)) setActiveBuildings(a.body.buildings);

      // Only surface an error banner if the *users* call failed AND we have
      // nothing to show. We deliberately ignore failures on active-buildings
      // alone (the modal dropdown will just be empty) and on buildings (the
      // Buildings tab will say "no buildings"). Either of those still being
      // empty is a useful signal in the UI itself rather than a scary red
      // banner that conflicts with a fully-rendered users table.
      if (u.status === 403 && !Array.isArray(u.body?.users)) {
        setError('Your session does not have permission to manage accounts. Sign out and back in to refresh your role.');
      } else if (u.status >= 400 && !Array.isArray(u.body?.users)) {
        setError(u.body?.error || u.body?.msg || `Couldn't load users (HTTP ${u.status}).`);
      }
    } catch (e) {
      setError(e.message || 'Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  }, [tryRefreshToken]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  useEffect(() => {
    const refresh = () => fetchAll();
    const storage = e => { if (e.key === 'buildingProfileUpdated') refresh(); };
    window.addEventListener('focus', refresh);
    window.addEventListener('building-profile-updated', refresh);
    window.addEventListener('storage', storage);
    return () => { window.removeEventListener('focus', refresh); window.removeEventListener('building-profile-updated', refresh); window.removeEventListener('storage', storage); };
  }, [fetchAll]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  // ----- table-local sort state for Users tab ------------------------------
  const [userSort, setUserSort] = useState({ key: 'last_login_at', dir: 'desc' });

  // ----- derived: filtered + sorted users ----------------------------------
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = users.filter((u) => {
      if (roleFilter !== 'all' && String(u.role || '').toLowerCase() !== String(roleFilter).toLowerCase()) return false;
      if (q) {
        const hay = `${u.username} ${u.email} ${u.first_name} ${u.last_name} ${u.management_company}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    // Sort. Each `getter` returns a primitive comparable. We treat missing
    // values as "least" when sorting ascending so empty fields cluster at
    // the bottom; reversed under desc by the `dir === 'asc' ? 1 : -1`.
    const { key, dir } = userSort;
    const getters = {
      name: (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase() || (u.username || '').toLowerCase(),
      username: (u) => (u.username || '').toLowerCase(),
      role: (u) => (u.role || '').toLowerCase(),
      buildings: (u) => (u.buildings || []).length,
      last_login_at: (u) => (u.last_login_at ? new Date(u.last_login_at).getTime() : 0),
    };
    const get = getters[key] || getters.name;
    const sign = dir === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
      return 0;
    });
  }, [users, search, roleFilter, userSort]);

  const toggleUserSort = (key) => {
    setUserSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'last_login_at' || key === 'buildings' ? 'desc' : 'asc' }));
  };

  // ----- buildings sort + filter state -------------------------------------
  const [buildingSort, setBuildingSort] = useState({ key: 'name', dir: 'asc' });
  const toggleBuildingSort = (key) => {
    setBuildingSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'updatedAt' || key === 'contracts' ? 'desc' : 'asc' }));
  };
  // Contract-type filter for the buildings tab — used to narrow the table to
  // e.g. "buildings with a mechanical-room contract on file".
  const [contractTypeFilter, setContractTypeFilter] = useState('all');
  // Region filter — derived dynamically from the data so we only show
  // regions that actually exist.
  const [regionFilter, setRegionFilter] = useState('all');
  const [managementFilter, setManagementFilter] = useState('all');
  const knownManagementCompanies = useMemo(() => {
    const companies = new Map();
    buildings.forEach((building) => {
      const name = canonicalCompany(building.managementCompany);
      if (name) companies.set(name.toLowerCase(), name);
    });
    return [...companies].sort((a, b) => a[1].localeCompare(b[1]));
  }, [buildings]);
  const hasUnassignedManagement = buildings.some((b) => !canonicalCompany(b.managementCompany));
  const knownRegions = useMemo(() => {
    const s = new Set();
    buildings.forEach((b) => { if (b.region) s.add(b.region); });
    return Array.from(s).sort();
  }, [buildings]);

  const filteredBuildings = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = buildings.filter((b) => {
      if (statusFilter !== 'all' && String(b.status || '').toLowerCase() !== statusFilter) return false;
      if (regionFilter !== 'all' && String(b.region || '').toLowerCase() !== regionFilter.toLowerCase()) return false;
      if (managementFilter !== 'all') {
        const company = canonicalCompany(b.managementCompany).toLowerCase();
        if (managementFilter === '__unassigned' ? !!company : company !== managementFilter) return false;
      }
      if (contractTypeFilter !== 'all') {
        const haveContracts = Array.isArray(b.contractData) ? b.contractData : [];
        const types = haveContracts.map((c) => String(c?.type || '').toLowerCase());
        if (!types.includes(contractTypeFilter)) return false;
      }
      if (q) {
        const hay = `${b.name} ${b.address} ${b.manager} ${b.managerEmail} ${b.region} ${b.buildingId}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    // Sort
    const { key, dir } = buildingSort;
    const getters = {
      name: (b) => (b.name || b.address || '').toLowerCase(),
      manager: (b) => (b.manager || '').toLowerCase(),
      status: (b) => (b.status || '').toLowerCase(),
      contracts: (b) => (Array.isArray(b.contractData) ? b.contractData.length : 0),
      jobberClient: (b) => (b.jobberClientName || '').toLowerCase(),
      updatedAt: (b) => (b.updatedAt ? new Date(b.updatedAt).getTime() : 0),
    };
    const get = getters[key] || getters.name;
    const sign = dir === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
      return 0;
    });
  }, [buildings, search, statusFilter, regionFilter, managementFilter, contractTypeFilter, buildingSort]);

  // ----- stat counts --------------------------------------------------------
  const stats = useMemo(() => {
    const internal = users.filter((u) => isMaintenanceRole(u.role)).length;
    const managers = users.filter((u) => {
      const r = String(u.role || '').toLowerCase();
      return r === 'manager' || r === 'regional' || r === 'corporate';
    }).length;
    const active = buildings.filter((b) => String(b.status || '').toLowerCase() === 'active').length;
    // Count contracts from contractData rows on active buildings only — that's
    // what "live work" actually means (pending/inactive buildings have stale
    // or pre-signed entries that don't represent serviceable contracts yet).
    let totalContracts = 0;
    for (const b of buildings) {
      if (String(b.status || '').toLowerCase() !== 'active') continue;
      if (Array.isArray(b.contractData)) totalContracts += b.contractData.length;
    }
    return {
      totalUsers: users.length,
      internal,
      managers,
      activeBuildings: active,
      totalBuildings: buildings.length,
      totalContracts,
    };
  }, [users, buildings]);

  // ----- handlers -----------------------------------------------------------
  const handleUserSaved = useCallback((u, kind) => {
    setUsers((prev) => {
      const idx = prev.findIndex((x) => x.objectId === u.objectId);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = u;
        return next;
      }
      return [u, ...prev];
    });
    showToast(`User ${kind}.`);
  }, []);

  const handleBuildingSaved = useCallback(async (b, kind) => {
    // refetch to keep buildingId etc. fresh from the server (esp. on create)
    await fetchAll();
    showToast(`Building ${kind}.`);
  }, [fetchAll]);

  const handleContractSaved = useCallback(async (b) => {
    await fetchAll();
    showToast(`Contract added to ${b.name || b.address}.`);
  }, [fetchAll]);

  // Helpers used by BuildingModal to dispatch edit / renew. We close the
  // BuildingModal first so the new modal mounts cleanly, but stash
  // `returnToBuilding: true` so we can reopen it once the user finishes.
  const openEditContract = useCallback((building, idx, contract) => {
    const wasBuildingOpen = !!bldgModal.open;
    if (wasBuildingOpen) setBldgModal({ open: false, editing: null });
    setContractEditModal({ open: true, building, contract, contractIndex: idx, returnToBuilding: wasBuildingOpen });
  }, [bldgModal.open]);

  const openRenewContract = useCallback((building, idx, contract) => {
    const wasBuildingOpen = !!bldgModal.open;
    if (wasBuildingOpen) setBldgModal({ open: false, editing: null });
    setContractRenewModal({ open: true, building, contract, contractIndex: idx, returnToBuilding: wasBuildingOpen });
  }, [bldgModal.open]);

  const handleContractEdited = useCallback(async (updatedBuilding, updatedContract, action) => {
    const shouldReopen = contractEditModal.returnToBuilding;
    await fetchAll();
    // If the user opened this from the BuildingModal, reopen that modal
    // with the freshly-updated building so they can keep editing other
    // fields without losing their place.
    if (shouldReopen) {
      setBldgModal({ open: true, editing: updatedBuilding });
    }
    showToast(action === 'edited'
      ? `Contract updated on ${updatedBuilding.name || updatedBuilding.address}.`
      : `Contract saved.`);
  }, [contractEditModal.returnToBuilding, fetchAll]);

  const handleContractRenewed = useCallback(async (updatedBuilding, updatedContract) => {
    const shouldReopen = contractRenewModal.returnToBuilding;
    await fetchAll();
    if (shouldReopen) {
      setBldgModal({ open: true, editing: updatedBuilding });
    }
    showToast(`Renewal saved · new expiry ${fmtDateShort(updatedContract.expiryDate)}.`);
  }, [contractRenewModal.returnToBuilding, fetchAll]);

  // ----- render -------------------------------------------------------------
  return (
    <div className="manage-accounts-page" style={{ background: C.bg, color: C.ink, fontFamily: sharedFont, minHeight: '100vh' }}>
      <div className="manage-accounts-page__body" style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 24px 64px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div>
            <div style={{ color: C.muted, fontSize: 12.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>Hudson Condominium Solutions Inc.</div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: C.navy, margin: '4px 0 4px', letterSpacing: '-0.01em' }}>Manage Accounts</h1>
            <div style={{ color: C.muted, fontSize: 13 }}>Single place to add buildings, contracts, and portal users.</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <SecondaryButton onClick={() => setContractModal({ open: true, defaultBuildingObjectId: '' })}>
              <FaFileContract /> New contract
            </SecondaryButton>
            <SecondaryButton onClick={() => setBldgModal({ open: true, editing: null })}>
              <FaBuilding /> New building
            </SecondaryButton>
            <PrimaryButton onClick={() => setUserModal({ open: true, editing: null })}>
              <FaUserPlus /> New user
            </PrimaryButton>
          </div>
        </div>

        {/* Stat cards row */}
        <div className="ma-stats" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
          <Pill
            label="Total users"
            value={loading ? '—' : stats.totalUsers}
            sublabel="User profiles"
            accent={C.navy}
            icon={<FaUserCircle />}
          />
          <Pill
            label="Total buildings"
            value={loading ? '—' : stats.activeBuildings}
            sublabel="Active accounts"
            accent={C.green}
            icon={<FaBuilding />}
          />
          <Pill
            label="Total contracts"
            value={loading ? '—' : stats.totalContracts}
            sublabel="Across all active buildings"
            accent={C.navy}
            icon={<FaFileContract />}
          />
          <div className="ma-onboarding-card"
            style={{
              flex: '1 1 280px',
              minWidth: 0,
              background: `linear-gradient(135deg, ${C.navy} 0%, #16294C 100%)`,
              color: '#fff',
              borderRadius: 12,
              padding: '18px 22px',
              boxShadow: C.shadow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', opacity: 0.85 }}>Need to onboard someone?</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>Create their portal access in 30 seconds.</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>Set username, password, role, and which buildings they can see.</div>
            </div>
            <button
              type="button"
              onClick={() => setUserModal({ open: true, editing: null })}
              style={{
                background: '#fff',
                color: C.navy,
                border: 'none',
                fontWeight: 700,
                fontSize: 13,
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >+ Add user</button>
          </div>
        </div>

        {/* Filter strip */}
        <div style={{
          background: '#fff',
          border: `1px solid ${C.line}`,
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 14,
          boxShadow: C.shadow,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 6, background: C.lineSoft, padding: 4, borderRadius: 10 }}>
              {['users', 'buildings'].map((t) => {
                const on = tab === t;
                return (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setTab(t)}
                    style={{
                      background: on ? '#fff' : 'transparent',
                      border: 'none',
                      padding: '8px 16px',
                      fontWeight: 600,
                      color: on ? C.navy : C.muted,
                      fontSize: 13,
                      borderRadius: 8,
                      cursor: 'pointer',
                      boxShadow: on ? '0 1px 3px rgba(20,30,60,.08)' : 'none',
                      textTransform: 'capitalize',
                    }}
                  >
                    {t} ({t === 'users' ? users.length : buildings.length})
                  </button>
                );
              })}
            </div>
            <div className="ma-filters" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {tab === 'users' ? (
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={{ ...inputStyle, width: 'auto', minWidth: 160 }}
                >
                  <option value="all">All roles</option>
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              ) : (
                <>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ ...inputStyle, width: 'auto', minWidth: 140 }}
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <select
                    value={contractTypeFilter}
                    onChange={(e) => setContractTypeFilter(e.target.value)}
                    style={{ ...inputStyle, width: 'auto', minWidth: 180 }}
                    title="Show buildings that have at least one contract of this type"
                  >
                    <option value="all">All contract types</option>
                    {CONTRACT_TYPES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  {knownRegions.length > 1 ? (
                    <select
                      value={regionFilter}
                      onChange={(e) => setRegionFilter(e.target.value)}
                      style={{ ...inputStyle, width: 'auto', minWidth: 140 }}
                    >
                      <option value="all">All regions</option>
                      {knownRegions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : null}
                  <select
                    aria-label="Filter by management company"
                    value={managementFilter}
                    onChange={(e) => setManagementFilter(e.target.value)}
                    style={{ ...inputStyle, width: 220 }}
                  >
                    <option value="all">All management companies</option>
                    {knownManagementCompanies.map(([value, name]) => (
                      <option key={value} value={value}>{name}</option>
                    ))}
                    {hasUnassignedManagement ? <option value="__unassigned">Management not on file</option> : null}
                  </select>
                </>
              )}
              <div style={{ position: 'relative' }}>
                <FaSearch style={{ position: 'absolute', left: 12, top: 13, color: C.muted, fontSize: 12 }} />
                <input
                  type="text"
                  placeholder={tab === 'users' ? 'Search users…' : 'Search buildings…'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ ...inputStyle, width: 280, paddingLeft: 32 }}
                />
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div style={{ background: C.redSoft, color: C.red, padding: '12px 16px', borderRadius: 10, fontSize: 13.5, marginBottom: 14 }}>
            {error}
          </div>
        ) : null}

        {/* Table */}
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.line}`, boxShadow: C.shadow, overflow: 'hidden' }}>
          {tab === 'users' ? (
            <UsersTable

              users={filteredUsers}
              loading={loading}
              onEdit={(u) => setUserModal({ open: true, editing: u })}
              sort={userSort}
              onSort={toggleUserSort}
            />
          ) : (
            <BuildingsTable

              buildings={filteredBuildings}
              loading={loading}
              onEdit={(b) => setBldgModal({ open: true, editing: b })}
              onAddContract={(b) => setContractModal({ open: true, defaultBuildingObjectId: b?.objectId || '' })}
              // Patch a single building in place after a signed-contract
              // upload / delete — avoids a full /api/buildings refetch
              // for what's essentially a one-field change.
              onBuildingChanged={(objectId, patch) =>
                setBuildings((prev) =>
                  prev.map((b) => (b.objectId === objectId ? { ...b, ...patch } : b))
                )
              }
              sort={buildingSort}
              onSort={toggleBuildingSort}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <UserModal
        onProfileSaved={fetchAll}
        open={userModal.open}
        onClose={() => setUserModal({ open: false, editing: null })}
        editing={userModal.editing}
        allBuildings={activeBuildings}
        onSaved={handleUserSaved}
        onAddContractForBuilding={(b) => {
          // Close user modal then open the contract modal pre-filled with the
          // building they wanted to add a contract for. On save, we re-fetch
          // to pick up new contractData and re-open the user modal would lose
          // their unsaved edits — so we deliberately don't reopen it here.
          setUserModal({ open: false, editing: null });
          setContractModal({ open: true, defaultBuildingObjectId: b?.objectId || '' });
        }}
      />
      <BuildingModal
        onProfileSaved={fetchAll}
        open={bldgModal.open}
        onClose={() => setBldgModal({ open: false, editing: null })}
        editing={bldgModal.editing}
        onSaved={handleBuildingSaved}
        onBuildingChanged={(objectId, patch) =>
          setBuildings((prev) =>
            prev.map((b) => (b.objectId === objectId ? { ...b, ...patch } : b))
          )
        }
        onAddContract={(b) => {
          // Close the building modal first so the contract modal mounts cleanly
          // and pre-selects this building.
          setBldgModal({ open: false, editing: null });
          setContractModal({ open: true, defaultBuildingObjectId: b?.objectId || '' });
        }}
        onEditContract={openEditContract}
        onRenewContract={openRenewContract}
      />
      <ContractModal
        open={contractModal.open}
        onClose={() => setContractModal({ open: false, defaultBuildingObjectId: '' })}
        buildings={buildings.filter((b) => String(b.status || '').toLowerCase() === 'active')}
        defaultBuildingObjectId={contractModal.defaultBuildingObjectId}
        onSaved={handleContractSaved}
      />
      <ContractEditModal
        open={contractEditModal.open}
        onClose={() => setContractEditModal({ open: false, building: null, contract: null, contractIndex: null, returnToBuilding: false })}
        building={contractEditModal.building}
        contract={contractEditModal.contract}
        contractIndex={contractEditModal.contractIndex}
        onSaved={handleContractEdited}
      />
      <ContractRenewModal
        open={contractRenewModal.open}
        onClose={() => setContractRenewModal({ open: false, building: null, contract: null, contractIndex: null, returnToBuilding: false })}
        building={contractRenewModal.building}
        contract={contractRenewModal.contract}
        contractIndex={contractRenewModal.contractIndex}
        onSaved={handleContractRenewed}
      />

      {toast ? (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: C.green,
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 999,
            fontWeight: 600,
            fontSize: 13,
            boxShadow: '0 8px 24px rgba(28, 149, 80, .35)',
            zIndex: 1500,
          }}
        >
          <FaCheck style={{ marginRight: 8 }} />{toast}
        </div>
      ) : null}
    </div>
  );
}

// ---------- sub-tables -----------------------------------------------------

function thStyle(extra) {
  return {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 11.5,
    fontWeight: 700,
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    background: '#FAFBFD',
    borderBottom: `1px solid ${C.line}`,
    ...extra,
  };
}

function tdStyle(extra) {
  return {
    padding: '14px 16px',
    fontSize: 13.5,
    color: C.ink,
    borderBottom: `1px solid ${C.lineSoft}`,
    verticalAlign: 'middle',
    ...extra,
  };
}

function SortHeader({ label, sortKey, sort, onSort, width, align = 'left' }) {
  const active = sort?.key === sortKey;
  const Icon = !active ? FaSort : sort.dir === 'asc' ? FaSortUp : FaSortDown;
  return (
    <th style={thStyle({ width, textAlign: align })}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        style={{
          background: 'transparent',
          border: 'none',
          color: active ? C.navy : C.muted,
          padding: 0,
          font: 'inherit',
          fontWeight: 700,
          fontSize: 11.5,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
        }}
      >
        {label}
        <Icon size={10} style={{ opacity: active ? 1 : 0.55 }} />
      </button>
    </th>
  );
}

function PasswordCell({ value, onResetClick }) {
  const [revealed, setRevealed] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const hasValue = !!value;

  // Copy the plaintext to the OS clipboard. We use the async Clipboard API
  // when available and fall back to a hidden textarea + execCommand for
  // older browsers / non-secure contexts (Cursor's localhost works fine
  // either way). Briefly flip an icon to confirm.
  const doCopy = React.useCallback(async (e) => {
    e.stopPropagation();
    if (!value) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error('[PasswordCell] copy failed', err);
    }
  }, [value]);

  if (!hasValue) {
    // Pre-existing accounts may have no `userpass` mirror yet. Make the
    // cell actionable rather than dead — click opens the edit modal so the
    // back-office can issue a new password (which then shows up here on
    // subsequent loads).
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (onResetClick) onResetClick(); }}
        title="No password on file — click to set one"
        style={{
          background: 'transparent',
          border: `1px dashed ${C.line}`,
          borderRadius: 6,
          color: C.muted,
          fontSize: 11.5,
          fontWeight: 600,
          padding: '3px 8px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        }}
      ><FaPen size={9} />Set</button>
    );
  }
  return (
    <div
      style={{
        // Keep the controls beside the value; long revealed passwords
        // can wrap within their own space without moving the controls.
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        flexWrap: 'nowrap',
      }}
    >
      <span
        title={revealed ? value : 'Click the eye to reveal'}
        style={{
          fontFamily: revealed ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'monospace',
          fontSize: revealed ? 12.5 : 13.5,
          color: revealed ? C.ink : C.inkSoft,
          letterSpacing: revealed ? 0 : 1,
          // No truncation — wrap the value if it's long, so passwords
          // with 20+ chars stay fully visible when revealed.
          whiteSpace: revealed ? 'normal' : 'nowrap',
          overflow: 'visible',
          textOverflow: 'clip',
          wordBreak: 'break-all',
          flex: '1 1 auto',
          minWidth: 0,
          fontWeight: revealed ? 600 : 400,
          userSelect: revealed ? 'text' : 'none',
          cursor: revealed ? 'text' : 'default',
        }}
      >{revealed ? value : '••••••••'}</span>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setRevealed((v) => !v); }}
          title={revealed ? 'Hide password' : 'Reveal password'}
          aria-label={revealed ? 'Hide password' : 'Reveal password'}
          style={{
            background: 'transparent',
            border: 'none',
            color: C.muted,
            cursor: 'pointer',
            padding: 3,
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 4,
          }}
        >{revealed ? <FaEyeSlash size={12} /> : <FaEye size={12} />}</button>
        <button
          type="button"
          onClick={doCopy}
          title={copied ? 'Copied!' : 'Copy password to clipboard'}
          aria-label="Copy password"
          style={{
            background: copied ? C.greenSoft : 'transparent',
            border: 'none',
            color: copied ? C.green : C.muted,
            cursor: 'pointer',
            padding: 3,
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 4,
            transition: 'background 120ms ease, color 120ms ease',
          }}
        >{copied ? <FaCheck size={11} /> : <FaRegCopy size={12} />}</button>
      </div>
    </div>
  );
}

// ---------- shared row-action atoms ---------------------------------------

// Open the manager-facing preview for a given building in a new tab. Mirrors
// the same URL shape used by MaintenanceHome.openBuildingPreview so the
// "log in as the manager" flow is consistent across the hub.
function openBuildingPreview(buildingId) {
  if (!buildingId) return;
  const url = `${window.location.origin}/?buildingId=${encodeURIComponent(buildingId)}&preview=1`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Square icon button matching the existing Edit pen style. Disabled when
// no building is available to preview (e.g. a user with no assignments).
function ManagerViewButton({ buildingId, title, disabled = false }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) openBuildingPreview(buildingId);
      }}
      title={title}
      aria-label={title}
      disabled={disabled}
      style={{
        background: disabled ? C.lineSoft : '#fff',
        border: `1px solid ${C.line}`,
        color: disabled ? C.muted : C.navy,
        padding: '6px 8px',
        borderRadius: 8,
        fontSize: 12.5,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    ><FaExternalLinkAlt size={11} /></button>
  );
}

// Signed-contract thumbnail tied to ONE contract row inside a building's
// contractData array. Renders three states:
//
//   1. No file → dashed-border "+" tile, click triggers a file picker and
//      POSTs to /api/buildings/:id/upload-signed-contract with the
//      contractIndex so the backend splices the URL into contractData[i].
//   2. Uploading → spinner-like tile, prevents duplicate uploads.
//   3. File on file → PDF icon tile with filename tooltip; click opens
//      the Parse Files URL in a new tab. When `showRemove` is true a
//      small red trash button overlays the corner.
//
// onChange is fired after a successful upload OR delete with the updated
// contractData array so the parent can patch its building state in place
// without a full refetch.
function SignedContractCell({
  building,
  contract,
  contractIndex,
  size = 44,
  showRemove = false,
  onChange,
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = React.useRef(null);

  // The contract's own signed-contract URL is the source of truth.
  const signedUrl = contract?.signedContractUrl || '';
  const signedName = contract?.signedContractName || contract?.signedContractFileName || '';
  const hasFile = !!signedUrl;

  const triggerPick = () => {
    setErr('');
    if (uploading) return;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !building?.objectId || contractIndex == null) return;
    setUploading(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('contractIndex', String(contractIndex));
      const res = await fetch(
        `${API_BASE_URL}/api/buildings/${encodeURIComponent(building.objectId)}/upload-signed-contract`,
        { method: 'POST', body: fd }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Upload failed (${res.status})`);
      // Server returns the full updated contractData so we can patch
      // both the row state AND the open modal in one shot.
      onChange?.(body.contractData || null, body.contract || null);
    } catch (e) {
      setErr(e.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async (e) => {
    e.stopPropagation();
    if (!building?.objectId || contractIndex == null || uploading) return;
    if (!window.confirm('Remove the signed contract for this row?')) return;
    setUploading(true);
    setErr('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/buildings/${encodeURIComponent(building.objectId)}/signed-contract?contractIndex=${contractIndex}`,
        { method: 'DELETE' }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Remove failed (${res.status})`);
      onChange?.(body.contractData || null, null);
    } catch (e) {
      setErr(e.message || 'Remove failed.');
    } finally {
      setUploading(false);
    }
  };

  const tileBase = {
    width: size,
    height: size,
    borderRadius: 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flexShrink: 0,
    transition: 'background 120ms ease, border-color 120ms ease',
  };

  // Empty / "click to add" state.
  if (!hasFile && !uploading) {
    return (
      <div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); triggerPick(); }}
          title="Add the signed contract (PDF, JPG, or PNG)"
          aria-label="Add signed contract"
          style={{
            ...tileBase,
            background: '#fff',
            border: `1.5px dashed ${C.line}`,
            color: C.muted,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.color = C.navy; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.line; e.currentTarget.style.color = C.muted; }}
        >
          <FaPlus size={14} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={onFile}
          style={{ display: 'none' }}
        />
        {err ? (
          <div style={{ marginTop: 4, fontSize: 11, color: C.red, maxWidth: 160 }}>{err}</div>
        ) : null}
      </div>
    );
  }

  // Uploading state.
  if (uploading) {
    return (
      <div
        style={{
          ...tileBase,
          background: C.blueSoft,
          border: `1.5px dashed ${C.blue}`,
          color: C.blue,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '.05em',
        }}
        title="Uploading…"
      >
        …
      </div>
    );
  }

  // Have-file state — clickable thumb opens the PDF.
  return (
    <div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (signedUrl) window.open(signedUrl, '_blank', 'noopener,noreferrer');
        }}
        title={`View signed contract: ${signedName || 'file'}`}
        aria-label="View signed contract"
        style={{
          ...tileBase,
          background: C.redSoft,
          border: `1.5px solid ${C.red}`,
          color: C.red,
          cursor: 'pointer',
        }}
      >
        <FaFilePdf size={Math.round(size * 0.45)} />
        {showRemove ? (
          <span
            role="button"
            tabIndex={0}
            onClick={onRemove}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRemove(e); } }}
            title="Remove signed contract"
            aria-label="Remove signed contract"
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 18,
              height: 18,
              borderRadius: 999,
              background: '#fff',
              border: `1px solid ${C.line}`,
              color: C.red,
              fontSize: 9,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(20,30,60,.12)',
            }}
          >
            <FaTrash size={8} />
          </span>
        ) : null}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        onChange={onFile}
        style={{ display: 'none' }}
      />
      {err ? (
        <div style={{ marginTop: 4, fontSize: 11, color: C.red, maxWidth: 160 }}>{err}</div>
      ) : null}
    </div>
  );
}

function UsersTable({ users, loading, onEdit, sort, onSort }) {
  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>Loading accounts…</div>;
  }
  if (users.length === 0) {
    return <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>No users match your filters.</div>;
  }
  return (
    <div className="ma-users-container" style={{overflowX:'auto'}}>
      <table role="table" className="ma-users-table" style={{ minWidth: 1100, width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '20%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '10%' }} />
        </colgroup>
        <thead>
          <tr role="row">
            <SortHeader label="Name" sortKey="name" sort={sort} onSort={onSort} />
            <SortHeader label="Username / email" sortKey="username" sort={sort} onSort={onSort} />
            <th style={thStyle()}>Password</th>
            <SortHeader label="Buildings" sortKey="buildings" sort={sort} onSort={onSort} />
            <SortHeader label="Role" sortKey="role" sort={sort} onSort={onSort} />
            <SortHeader label="Last activity" sortKey="last_login_at" sort={sort} onSort={onSort} />
            <th style={thStyle({ textAlign: 'right' })}> </th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || '—';
            const protectedAdmin = isL3Role(u.role) && !currentAdminCanGrantMaintenance();
            const bldgs = u.buildingDetails || [];
            const primary = u.role === 'manager' && bldgs.length === 1 ? bldgs[0] : null;
            return (
              <tr role="row" key={u.objectId}>
                <td role="cell" data-label="Name" style={tdStyle({paddingRight:24})}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 999, background: C.blueSoft, color: C.navy,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12.5, fontWeight: 700, flexShrink: 0,
                    }}>{initialsOf(u)}</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }} title={fullName}>{fullName}</div>
                      {primary?.profileUpdatedAt && <ProfileUpdated date={primary.profileUpdatedAt} byRole={primary.profileUpdatedByRole}/>}
                      <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }} title={primary?.managementCompany || u.management_company || ''}>{primary?.managementCompany || u.management_company || ''}</div>
                    </div>
                  </div>
                </td>
                <td role="cell" data-label="Username / email" style={tdStyle({ minWidth: 0 })}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={u.username || ''}>{u.username || '—'}</div>
                  <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={u.email || ''}>{u.email || '—'}</div>
                </td>
                <td role="cell" data-label="Password" style={tdStyle({ minWidth: 0, paddingLeft: 10, paddingRight: 10, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' })}>
                  {protectedAdmin ? <span style={{ color: C.muted, fontSize: 12 }}>Admin only</span> : <PasswordCell
                    value={u.password_plaintext}
                    onResetClick={() => onEdit(u)}
                  />}
                </td>
                <td role="cell" data-label="Buildings" style={tdStyle({ minWidth: 0 })}>
                  {bldgs.length === 0 ? (
                    <span style={{ color: C.muted, fontSize: 12.5 }}>None assigned</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minWidth: 0 }}>
                      {bldgs.slice(0, 2).map((b) => (
                        <span
                          key={b.buildingId}
                          title={`${b.name || `#${b.buildingId}`}${b.address ? ' — ' + b.address : ''}`}
                          style={{
                            background: C.lineSoft,
                            color: C.inkSoft,
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: 6,
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >{b.name || `#${b.buildingId}`} </span>
                      ))}
                      {bldgs.length > 2 ? (
                        <span style={{ color: C.muted, fontSize: 11.5, fontWeight: 600, padding: '3px 4px' }}>
                          +{bldgs.length - 2} more
                        </span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td role="cell" data-label="Role" style={tdStyle()}><RoleBadge role={u.role} /></td>
                <td role="cell" data-label="Last activity" style={tdStyle()}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }} title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : ''}>{relativeFromIso(u.last_login_at)}</div>
                  {u.last_login_at ? (
                    <div style={{ fontSize: 11.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {new Date(u.last_login_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                      {', '}
                      {new Date(u.last_login_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11.5, color: C.muted }}>No sign-ins</div>
                  )}
                </td>
                <td role="cell" data-label="Actions" style={tdStyle({ textAlign: 'right' })}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                    {/* Manager View — opens the manager-facing dashboard
                        for this user's first assigned building. Disabled
                        when no buildings are mapped to the user. */}
                    <ManagerViewButton
                      buildingId={bldgs[0]?.buildingId}
                      disabled={bldgs.length === 0}
                      title={
                        bldgs.length === 0
                          ? 'No building assigned'
                          : bldgs.length === 1
                          ? `Open manager view for ${bldgs[0].name || `#${bldgs[0].buildingId}`}`
                          : `Open manager view for ${bldgs[0].name || `#${bldgs[0].buildingId}`} (this user has ${bldgs.length} buildings — opens the first)`
                      }
                    />
                    <button
                      type="button"
                      onClick={() => onEdit(u)}
                      disabled={protectedAdmin}
                      title={protectedAdmin ? 'Admin required' : 'Edit user'}
                      aria-label={protectedAdmin ? 'Admin required' : 'Edit user'}
                      style={{
                        background: '#fff',
                        border: `1px solid ${C.line}`,
                        color: C.ink,
                        padding: '6px 8px',
                        borderRadius: 8,
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: protectedAdmin ? 'default' : 'pointer',
                        opacity: protectedAdmin ? 0.4 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    ><FaPen size={11} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Modal used from the buildings table to connect (or change) the Jobber
// client linked to a building. Saving PUTs the three jobber* fields on the
// Building record, so every future scheduled maintenance auto-attaches to
// this client on Jobber.
function JobberConnectModal({ building, onClose, onConnected }) {
  const [query, setQuery] = useState(building?.name || building?.address || '');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const search = async () => {
    setSearching(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/jobber/clients?q=${encodeURIComponent(query)}`);
      const body = await res.json().catch(() => ({}));
      if (!body.ok) throw new Error(body.error || 'Client search failed.');
      setResults(body.clients || []);
      setSearched(true);
    } catch (e) {
      setResults([]);
      setError(e.message || 'Client search failed.');
    } finally {
      setSearching(false);
    }
  };

  const saveLink = async (fields) => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/buildings/${building.objectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Save failed (${res.status})`);
      onConnected(fields);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save the Jobber connection.');
    } finally {
      setSaving(false);
    }
  };

  const connect = (client) => saveLink({
    jobberClientId: client.id,
    jobberPropertyId: client.propertyId || '',
    jobberClientName: client.name || '',
  });

  const disconnect = () => saveLink({
    jobberClientId: '', jobberPropertyId: '', jobberClientName: '',
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Connect Jobber client — ${building.name || building.address || `#${building.buildingId}`}`}
      subtitle="Once connected, every maintenance scheduled for this building is automatically attached to this Jobber client."
      width={560}
    >
      {building.jobberClientName ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
          padding: '10px 12px', marginBottom: 14,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '.04em' }}>Currently connected</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0c4a6e', marginTop: 2 }}>{building.jobberClientName}</div>
          </div>
          <button
            type="button"
            onClick={disconnect}
            disabled={saving}
            style={{
              background: '#fff', border: `1px solid ${C.line}`, color: C.red,
              borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >Remove</button>
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
          placeholder="Search Jobber clients…"
          autoFocus
          style={{
            flex: 1, border: `1px solid ${C.line}`, borderRadius: 8,
            padding: '9px 11px', fontSize: 13, fontFamily: 'inherit', outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          onClick={search}
          disabled={searching}
          style={{
            background: C.ink, color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 16px', fontSize: 13, fontWeight: 600,
            cursor: searching ? 'wait' : 'pointer',
          }}
        >{searching ? 'Searching…' : 'Search'}</button>
      </div>
      {error && <div style={{ color: C.red, fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>{error}</div>}
      <div style={{ marginTop: 12, maxHeight: 300, overflowY: 'auto' }}>
        {results.map((client) => (
          <button
            key={client.id}
            type="button"
            onClick={() => connect(client)}
            disabled={saving}
            style={{
              display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, textAlign: 'left', background: '#fff', border: `1px solid ${C.line}`,
              borderRadius: 8, padding: '10px 12px', marginBottom: 6,
              cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit',
            }}
          >
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: C.ink }}>{client.name}</span>
              {client.propertyAddress ? (
                <span style={{ display: 'block', fontSize: 11.5, color: C.muted, marginTop: 2 }}>{client.propertyAddress}</span>
              ) : null}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', whiteSpace: 'nowrap' }}>
              {saving ? 'Saving…' : 'Connect'}
            </span>
          </button>
        ))}
        {searched && !searching && results.length === 0 && !error && (
          <div style={{ color: C.muted, fontSize: 12.5, textAlign: 'center', padding: '18px 8px' }}>
            No Jobber clients matched “{query}”.
          </div>
        )}
      </div>
    </Modal>
  );
}

function BuildingsTable({ buildings, loading, onEdit, onAddContract, onBuildingChanged, sort, onSort }) {
  const [jobberModalBuilding, setJobberModalBuilding] = useState(null);
  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>Loading buildings…</div>;
  }
  if (buildings.length === 0) {
    return <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>No buildings match your filters.</div>;
  }
  return (
    <>
    <div className="ma-buildings-container">
      <table role="table" className="ma-buildings-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '13.1%' }} />
          <col style={{ width: '8.2%' }} />
          <col style={{ width: '17.2%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '17.2%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '12.3%' }} />
          <col style={{ width: '9.4%' }} />
          <col style={{ width: '6.6%' }} />
        </colgroup>
        <thead>
          <tr role="row">
            <SortHeader label="Building" sortKey="name" sort={sort} onSort={onSort} />
            <th style={thStyle()}>Management</th>
            <SortHeader label="Manager" sortKey="manager" sort={sort} onSort={onSort} />
            <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
            <SortHeader label="Contracts" sortKey="contracts" sort={sort} onSort={onSort} />
            <th style={thStyle()}>Signed Contract</th>
            <SortHeader label="Jobber Client" sortKey="jobberClient" sort={sort} onSort={onSort} />
            <SortHeader label="Last updated" sortKey="updatedAt" sort={sort} onSort={onSort} />
            <th style={thStyle({ textAlign: 'right' })}> </th>
          </tr>
        </thead>
        <tbody>
          {buildings.map((b) => {
            const contracts = b.contractData || [];
            return (
              <tr role="row" key={b.objectId || b.buildingId}>
                <td role="cell" data-label="Building" style={tdStyle({ minWidth: 0 })}>
                  <div style={{ fontWeight: 600, color: C.ink, whiteSpace: 'normal', overflowWrap: 'anywhere' }} title={b.name || b.address || `Building ${b.buildingId}`}>{b.name || b.address || `Building ${b.buildingId}`}</div>
                  <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'normal', overflowWrap: 'anywhere' }} title={b.address || ''}>{b.address || `ID ${b.buildingId}`}</div>
                  <div><ProfileUpdated date={b.profileUpdatedAt} byRole={b.profileUpdatedByRole}/></div>

                </td>
                <td role="cell" data-label="Management" style={tdStyle()}><CompanyBrand name={b.managementCompany} logoOnly/></td>
                <td role="cell" data-label="Manager" style={tdStyle({ minWidth: 0 })}>
                  <div style={{ fontWeight: 600, whiteSpace: 'normal', overflowWrap: 'anywhere' }} title={b.manager || ''}>{b.manager || '—'}</div>
                  <div className="ma-manager-email" style={{ fontSize: 12, color: C.muted }} title={b.managerEmail || ''}>{b.managerEmail || ''}</div>

                </td>
                <td role="cell" data-label="Status" style={tdStyle()}><StatusBadge status={b.status} /></td>
                <td role="cell" data-label="Contracts" style={tdStyle({ minWidth: 0 })}>
                  {contracts.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => onAddContract(b)}
                      style={{
                        background: 'transparent',
                        border: `1px dashed ${C.line}`,
                        color: C.muted,
                        padding: '6px 12px',
                        borderRadius: 8,
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >+ Add</button>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {contracts.slice(0, 2).map((c, i) => (
                        <span key={i} title={contractScopeLabel(c, prettyContractType(c.type))} style={{ background: C.greenSoft, color: C.green, fontSize: 11.5, fontWeight: 600, padding: '3px 8px', borderRadius: 6, whiteSpace: 'normal', overflowWrap: 'break-word', maxWidth: '100%' }}>
                          {contractScopeLabel(c, prettyContractType(c.type))}
                        </span>
                      ))}
                      {contracts.length > 2 ? (
                        <span style={{ color: C.muted, fontSize: 11.5, fontWeight: 600, padding: '3px 4px' }}>+{contracts.length - 2}</span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td role="cell" data-label="Signed contract" style={tdStyle()}>
                  {contracts.length === 0 ? (
                    <span style={{ color: C.muted, fontSize: 11.5 }} title="Add a contract first">—</span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      {contracts.slice(0, 3).map((c, ci) => (
                        <SignedContractCell
                          key={`sc-${ci}`}
                          building={b}
                          contract={c}
                          contractIndex={ci}
                          size={28}
                          onChange={(nextContractData) => {
                            if (Array.isArray(nextContractData)) {
                              onBuildingChanged?.(b.objectId, { contractData: nextContractData });
                            }
                          }}
                        />
                      ))}
                      {contracts.length > 3 ? (
                        <span
                          title={`${contracts.length - 3} more contract${contracts.length - 3 === 1 ? '' : 's'} — open Edit to manage`}
                          style={{ color: C.muted, fontSize: 11, fontWeight: 700 }}
                        >+{contracts.length - 3}</span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td role="cell" data-label="Jobber client" style={tdStyle({ minWidth: 0 })}>
                  {b.jobberClientName ? (
                    <button
                      type="button"
                      onClick={() => setJobberModalBuilding(b)}
                      title={`Connected to ${b.jobberClientName} — click to change`}
                      style={{
                        background: 'transparent', border: 'none', padding: 0,
                        cursor: 'pointer', textAlign: 'left', maxWidth: '100%',
                        fontFamily: 'inherit', display: 'block',
                      }}
                    >
                      <span style={{
                        display: 'inline-block', maxWidth: '100%',
                        background: '#f0f9ff', color: '#0369a1',
                        fontSize: 11.5, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                        whiteSpace: 'normal', overflowWrap: 'break-word',
                        verticalAlign: 'middle',
                      }}>
                        {b.jobberClientName}
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setJobberModalBuilding(b)}
                      title="Connect a Jobber client to this building"
                      style={{
                        background: 'transparent',
                        border: `1px dashed ${C.line}`,
                        color: C.muted,
                        padding: '6px 12px',
                        borderRadius: 8,
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >+ Connect</button>
                  )}
                </td>
                <td role="cell" data-label="Last updated" style={tdStyle()}>
                  {b.updatedAt ? (
                    <>
                      <div style={{ fontWeight: 600, whiteSpace: 'normal' }}>{relativeFromIso(b.updatedAt)}</div>
                      <div style={{ fontSize: 11.5, color: C.muted, whiteSpace: 'normal' }}>{new Date(b.updatedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</div>
                    </>
                  ) : <span style={{ color: C.muted, fontSize: 12.5 }}>—</span>}
                </td>
                <td role="cell" data-label="Actions" style={tdStyle({ textAlign: 'right' })}>
                  <div style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                    <ManagerViewButton
                      buildingId={b.buildingId}
                      disabled={!b.buildingId}
                      title={`Open manager view for ${b.name || b.address || `#${b.buildingId}`}`}
                    />
                    <button
                      type="button"
                      onClick={() => onEdit(b)}
                      title="Edit building"
                      aria-label="Edit building"
                      style={{
                        background: '#fff',
                        border: `1px solid ${C.line}`,
                        color: C.ink,
                        padding: '6px 8px',
                        borderRadius: 8,
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    ><FaPen size={11} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
      {jobberModalBuilding && (
        <JobberConnectModal
          building={jobberModalBuilding}
          onClose={() => setJobberModalBuilding(null)}
          onConnected={(fields) => onBuildingChanged?.(jobberModalBuilding.objectId, fields)}
        />
      )}
    </>
  );
}
